import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError, money } from "../lib/api";
import { passwordPolicyError, PASSWORD_HELP_TEXT } from "../lib/passwordPolicy";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import StatusFiltro, { STATUS_FILTRO_DEFAULT, statusFiltroPassa } from "../components/StatusFiltro";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Trash2, Shield, Target, TrendingUp, Upload, Eye, EyeOff, Pencil, Power, PowerOff, Filter, X, Palette, RotateCcw, MessageCircle, Link2, Sparkles, Check, QrCode, Mail, CheckCircle2, AlertTriangle, Wifi, Building2, Landmark, FileText, ScrollText, Image as ImageIcon, Settings, ExternalLink, Copy } from "lucide-react";

// Guia de configuração Asaas (delta 2026-09-16) — PDF estático servido
// pelo próprio LOOTUS (frontend/public/docs/...), nunca um link externo;
// abre em nova aba, nunca fecha o modal nem descarta o formulário em
// preenchimento (é só um <a target="_blank">, sem nenhuma interação com
// o Dialog).
const ASAAS_GUIDE_URL = "/docs/guia-configuracao-asaas-lootus.pdf";
import { useAuth } from "../context/AuthContext";
import PermissionsDialog from "../components/PermissionsDialog";
import GoalDialog from "../components/GoalDialog";
import EditUserDialog from "../components/EditUserDialog";
import FiscalNfseConfig from "../components/FiscalNfseConfig";
import AnalystHistoryDialog from "../components/AnalystHistoryDialog";
import { applyBrandingVars, isValidHex, readableTextHex, computeBgVars, hslTripletToCss } from "../lib/branding";

// ---------- Auditoria: tradução, categorias/cores e diff before/after ----------
// (pacote pós-homologação, item 6) — mapeia os códigos técnicos já usados
// pelo backend (audit()/audit_logs) para rótulos amigáveis. O valor técnico
// original continua disponível (Badge mostra a tradução, o modal de
// detalhes traz o código como referência).
const AUDIT_OP_LABELS = {
  create: "Inclusão", update: "Alteração", update_blocked: "Alteração bloqueada",
  deactivate: "Inativação", approve: "Aprovação", approve_receipt: "Aprovação de comprovante",
  bank_reconcile: "Conciliação bancária", cancel: "Cancelamento", cancel_blocked: "Cancelamento bloqueado",
  cancel_delete: "Cancelamento com exclusão", delete_blocked: "Exclusão bloqueada",
  pay: "Pagamento", reject_approval: "Rejeição",
  reverse_boleto: "Estorno de boleto", signature_request: "Solicitação de assinatura",
  signature_resend: "Reenvio de assinatura", ticket_deleted: "Exclusão de ficha",
  // Ajustes Finais de Acabamento da V1, item 2 — "Login" isolado (sem verbo)
  // lia como um rótulo técnico, não como um evento; os demais rótulos
  // abaixo (first_access_*/password_reset_otp_*) preenchem lacunas reais
  // encontradas na revisão: eram operações já gravadas pelo backend, mas
  // sem tradução aqui, então apareciam com o slug técnico cru no badge.
  login: "Login realizado", login_blocked_company_inactive: "Login bloqueado (empresa inativa)",
  login_failed: "Tentativa de login mal-sucedida", login_inactive_attempt: "Tentativa de login (usuário inativo)",
  password_reset: "Senha redefinida",
  password_reset_otp_requested: "Código de verificação solicitado (redefinição de senha)",
  password_reset_otp_confirmed: "Código de verificação confirmado (redefinição de senha)",
  password_reset_otp_failed: "Código de verificação incorreto (redefinição de senha)",
  password_reset_otp_max_attempts: "Limite de tentativas do código excedido (redefinição de senha)",
  first_access_otp_requested: "Código de verificação enviado (primeiro acesso)",
  first_access_otp_confirmed: "Código de verificação confirmado (primeiro acesso)",
  first_access_otp_max_attempts: "Limite de tentativas do código excedido (primeiro acesso)",
  first_access_password_changed: "Senha definida (primeiro acesso)",
  receipt_uploaded: "Envio de comprovante", signature_rejected: "Assinatura rejeitada",
  signed: "Assinatura realizada",
  // Integração WhatsApp (Fase 1 — Fundação) — item 14: eventos legíveis,
  // sem termo técnico, para configuração/conexão/envio/reenvio/falha.
  whatsapp_connected: "Integração WhatsApp conectada",
  whatsapp_disconnected: "Integração WhatsApp desconectada",
  whatsapp_test_message: "Mensagem de teste do WhatsApp solicitada",
  whatsapp_signature_link_sent: "Link de assinatura enviado por WhatsApp",
  whatsapp_signature_link_resent: "Link de assinatura reenviado por WhatsApp",
  whatsapp_send_failed: "Falha no envio por WhatsApp",
  // Fundação Multi-Provider de Pagamentos — Faturamento operacional dos
  // tenants: conta de pagamento (Mercado Pago e futuros provedores) e
  // cobrança real gerada/sincronizada/paga/cancelada no provider.
  payment_account_created: "Conta de pagamento cadastrada",
  payment_account_updated: "Conta de pagamento atualizada",
  payment_account_credential_updated: "Credencial da conta de pagamento atualizada",
  payment_account_enabled: "Conta de pagamento ativada",
  payment_account_disabled: "Conta de pagamento desativada",
  payment_account_set_default: "Conta de pagamento definida como padrão",
  payment_account_unset_default: "Conta de pagamento deixou de ser padrão",
  payment_account_connection_tested: "Teste de conexão da conta de pagamento",
  provider_charge_created: "Boleto/PIX gerado no provedor",
  provider_charge_synced: "Situação sincronizada com o provedor",
  provider_charge_paid: "Pagamento confirmado pelo provedor",
  provider_charge_cancelled: "Cancelamento no provedor",
  provider_charge_error: "Falha ao gerar cobrança no provedor",
};
const opLabel = (op) => AUDIT_OP_LABELS[op] || op;

const AUDIT_ENTITY_LABELS = {
  ticket: "Ficha de Serviço", invoice: "Faturamento", boleto: "Boleto", boletos: "Boletos",
  client: "Cliente", user: "Usuário", company: "Empresa", company_settings: "Configurações da Empresa",
  group: "Grupo", product: "Produto/Sistema",
  branding: "Identidade visual", whatsapp_config: "Configuração de WhatsApp",
  tenant_payment_account: "Conta de pagamento",
};
const entityLabel = (e) => AUDIT_ENTITY_LABELS[e] || e || "-";

// Ajustes Finais de Acabamento da V1, item 2 — descrição do evento em
// linguagem funcional, sem nunca citar IDs/hashes/códigos internos e sem
// apresentar a empresa/tenant como se fosse o sujeito humano da ação (o
// evento sempre pertence à empresa autenticada — isso já é garantido pelo
// isolamento de list_audit — mas quem "fez" a ação é sempre um usuário ou,
// nos eventos de acesso sem sessão ainda estabelecida, o próprio
// evento de acesso/segurança, nunca "a empresa"). Eventos sem uma entidade
// de negócio associada (login, redefinição de senha, primeiro acesso) têm
// frase própria; os demais compõem "Operação — Recurso" a partir das
// traduções que já existem (opLabel/entityLabel), sem duplicar tradução.
const AUDIT_ACCESS_DESCRIPTIONS = new Set([
  "login", "login_blocked_company_inactive", "login_failed", "login_inactive_attempt",
  "password_reset", "password_reset_otp_requested", "password_reset_otp_confirmed",
  "password_reset_otp_failed", "password_reset_otp_max_attempts",
  "first_access_otp_requested", "first_access_otp_confirmed",
  "first_access_otp_max_attempts", "first_access_password_changed",
]);
const eventDescription = (a) => {
  if (AUDIT_ACCESS_DESCRIPTIONS.has(a.op) || !a.entity) return opLabel(a.op);
  return `${opLabel(a.op)} — ${entityLabel(a.entity)}`;
};

const AUDIT_CATEGORY_META = {
  inclusao: { label: "Inclusão", badge: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  alteracao: { label: "Alteração", badge: "bg-blue-100 text-blue-800", dot: "bg-blue-500" },
  exclusao: { label: "Exclusão/Cancelamento", badge: "bg-red-100 text-red-800", dot: "bg-red-500" },
  aprovacao: { label: "Aprovação", badge: "bg-teal-100 text-teal-800", dot: "bg-teal-500" },
  rejeicao: { label: "Rejeição", badge: "bg-orange-100 text-orange-800", dot: "bg-orange-500" },
  assinatura: { label: "Assinatura", badge: "bg-purple-100 text-purple-800", dot: "bg-purple-500" },
  financeiro: { label: "Financeiro / Faturamento / Conciliação", badge: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  acesso: { label: "Acesso / Autenticação", badge: "bg-slate-200 text-slate-800", dot: "bg-slate-500" },
  integracoes: { label: "Integrações", badge: "bg-cyan-100 text-cyan-800", dot: "bg-cyan-500" },
  outro: { label: "Outro", badge: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
};
const opCategory = (op) => {
  if (op === "create") return "inclusao";
  if (["update", "update_blocked"].includes(op)) return "alteracao";
  if (["deactivate", "cancel", "cancel_blocked", "cancel_delete", "delete_blocked", "ticket_deleted"].includes(op)) return "exclusao";
  if (["approve", "approve_receipt"].includes(op)) return "aprovacao";
  if (["reject_approval", "signature_rejected"].includes(op)) return "rejeicao";
  if (["signature_request", "signature_resend", "signed"].includes(op)) return "assinatura";
  if (["bank_reconcile", "pay", "reverse_boleto", "receipt_uploaded"].includes(op)) return "financeiro";
  if ([
    "login", "login_blocked_company_inactive", "login_failed", "login_inactive_attempt",
    "password_reset", "password_reset_otp_requested", "password_reset_otp_confirmed",
    "password_reset_otp_failed", "password_reset_otp_max_attempts",
    "first_access_otp_requested", "first_access_otp_confirmed",
    "first_access_otp_max_attempts", "first_access_password_changed",
  ].includes(op)) return "acesso";
  if ([
    "whatsapp_connected", "whatsapp_disconnected", "whatsapp_test_message",
    "whatsapp_signature_link_sent", "whatsapp_signature_link_resent", "whatsapp_send_failed",
  ].includes(op)) return "integracoes";
  return "outro";
};

const AUDIT_FIELD_LABELS = {
  razao_social: "Razão Social", nome_fantasia: "Nome Fantasia", cnpj: "CPF/CNPJ", email: "E-mail",
  telefone: "Telefone", endereco: "Endereço", status: "Status", active: "Ativo", role: "Perfil",
  name: "Nome", cliente_id: "Cliente", analista_id: "Analista", supervisor_id: "Supervisor",
  tipo_servico: "Tipo de Serviço", produto: "Produto/Sistema", data_servico: "Data do atendimento",
  hora_inicio: "Início", hora_fim: "Término", intervalo_min: "Intervalo (min)",
  horas_abonadas: "Horas abonadas", horas_realizadas: "Horas realizadas", horas_faturaveis: "Horas faturáveis",
  valor_hora: "Valor hora", valor_total: "Valor total", descricao: "Descrição", pendencias: "Pendências",
  proximos_passos: "Próximos passos", numero: "Número", vencimento: "Vencimento", valor: "Valor",
  cpf_cnpj: "CPF/CNPJ", codigo: "Código", contato: "Contato", cep: "CEP", cidade: "Cidade", estado: "UF",
  reason: "Motivo", observacoes: "Observações", nome: "Nome",
  destinatario: "Destinatário", whatsapp: "WhatsApp",
};
// Rótulos amigáveis dos códigos internos de origem do número de WhatsApp
// (item 10) — nunca exibir "service_ticket"/"client_registration" cru na
// interface; o backend já devolve origem_label pronto nos endpoints de
// envio, este mapa cobre a exibição do valor bruto quando só o código
// aparece (ex.: dentro do diff Antes → Depois da Auditoria).
const WHATSAPP_ORIGIN_LABELS = { service_ticket: "WhatsApp informado na ficha", client_registration: "Cadastro do cliente" };
const WHATSAPP_STATUS_META = {
  not_configured: { label: "Não configurado", badge: "bg-slate-100 text-slate-700" },
  connecting: { label: "Conectando", badge: "bg-amber-100 text-amber-800" },
  connected: { label: "Conectado", badge: "bg-emerald-100 text-emerald-800" },
  error: { label: "Erro", badge: "bg-red-100 text-red-800" },
  disconnected: { label: "Desconectado", badge: "bg-slate-200 text-slate-700" },
};
// Pacote Final de Ajustes, Parte G — mesmo vocabulário já usado no card
// homologado do Admin Master (ssl_tls|starttls|none).
const SMTP_SECURITY_LABEL = { ssl_tls: "SSL/TLS", starttls: "STARTTLS", none: "Sem TLS" };
const fieldLabel = (k) => AUDIT_FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
// Campos internos/técnicos que nunca aparecem no diff — inclusive um _id do
// Mongo eventualmente remanescente (já sanitizado pelo backend, mas a
// interface nunca deve depender só disso).
const AUDIT_DIFF_HIDDEN_KEYS = new Set(["id", "_id", "company_id", "created_at", "updated_at", "password_hash"]);
const fmtVal = (v, key) => {
  if (v === null || v === undefined || v === "") return "-";
  if (key === "origem" && WHATSAPP_ORIGIN_LABELS[v]) return WHATSAPP_ORIGIN_LABELS[v];
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};
function diffFields(before, after) {
  const keys = new Set([...(before ? Object.keys(before) : []), ...(after ? Object.keys(after) : [])]);
  const rows = [];
  for (const k of keys) {
    if (AUDIT_DIFF_HIDDEN_KEYS.has(k)) continue;
    const bv = before ? before[k] : undefined;
    const av = after ? after[k] : undefined;
    if (JSON.stringify(bv) === JSON.stringify(av)) continue;
    rows.push({ key: k, before: bv, after: av });
  }
  return rows;
}
const fmtDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("pt-BR");
};

const LOGO_MAX_MB = 2;

// Pré-visualização em tempo real da Personalização (Pacote 2, item 2;
// Correções, item 3: "deve refletir as MESMAS REGRAS utilizadas pelo
// ambiente real"). Por isso usa aqui os mesmos cálculos de lib/branding.js
// (computeBgVars, readableTextHex) e o mesmo mapeamento de cores por
// elemento que index.css/Layout.jsx aplicam de verdade: principal → menu
// ativo + botão + barra superior; destaque → texto de destaque; apoio →
// hover do menu. Continua sendo estilo INLINE a partir do estado do
// FORMULÁRIO (ainda não salvo) — nunca as variáveis CSS globais — para uma
// cor inválida/incompleta digitada no meio da edição nunca "vazar" para o
// resto da tela antes de "Salvar personalização".
function PersonalizacaoPreview({ branding, nomeFallback, logoUrl }) {
  const primary = isValidHex(branding.cor_principal) ? branding.cor_principal : "#3B82F6";
  const accent = isValidHex(branding.cor_destaque) ? branding.cor_destaque : "#2563EB";
  const support = isValidHex(branding.cor_apoio) ? branding.cor_apoio : "#E2E8F0";
  const primaryText = readableTextHex(primary);
  const bgVars = computeBgVars(branding.tema_fundo, branding.cor_principal);
  const bg = hslTripletToCss(bgVars.light) || "#F8FAFC"; // "claro" (padrão) — mesmo valor de --background em index.css
  const name = (branding.nome_fantasia && branding.nome_fantasia.trim()) || (nomeFallback && nomeFallback.trim()) || "LOOTUS";
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" style={{ background: bg }} data-testid="branding-preview">
      {/* barra superior — mesma regra do header real (Layout.jsx): traço na cor principal */}
      <div className="h-6 bg-white border-b-2 flex items-center justify-end px-2" style={{ borderBottomColor: `${primary}59` }}>
        <div className="w-3 h-3 rounded-full" style={{ background: primary }} />
      </div>
      <div className="flex">
        <div className="w-28 shrink-0 bg-white border-r border-slate-200 p-2 space-y-1.5">
          <div className="flex items-center gap-1.5 mb-2">
            {logoUrl ? <img src={logoUrl} alt="" className="w-5 h-5 rounded object-contain" /> : <div className="w-5 h-5 rounded" style={{ background: primary }} />}
            <span className="text-[10px] font-heading font-bold truncate">{name}</span>
          </div>
          <div className="text-[10px] rounded px-2 py-1 font-medium" style={{ background: `${primary}1A`, color: primary, borderLeft: `2px solid ${primary}` }}>Dashboard</div>
          <div className="text-[10px] rounded px-2 py-1 text-slate-500" style={{ background: `${support}80` }}>Clientes (apoio)</div>
          <div className="text-[10px] rounded px-2 py-1 text-slate-500">Fichas</div>
        </div>
        <div className="flex-1 p-3 space-y-2.5">
          <div className="rounded-md p-2 bg-white border border-slate-200">
            <div className="text-[9px] text-slate-400">Card de exemplo</div>
            <div className="text-[11px] font-semibold" style={{ color: accent }}>Destaque em cor de destaque</div>
          </div>
          <span className="text-[11px] px-3 py-1.5 rounded-md font-medium inline-block" style={{ background: primary, color: primaryText }}>Botão principal</span>
        </div>
      </div>
    </div>
  );
}

export default function Configuracoes() {
  const { user, plan, hasFeature, hasPerm } = useAuth();
  const isAdmin = user?.role === "admin";
  // Cadastros de Grupos e Produtos/Sistemas passam a ser controlados por
  // PERMISSÃO individual (groups:*/products:*), não mais por "é admin" —
  // é o que permite conceder o cadastro a um Supervisor sem promovê-lo.
  const podeCriarGrupo = hasPerm("groups:create");
  const podeEditarGrupo = hasPerm("groups:edit");
  const podeCriarProduto = hasPerm("products:create");
  const podeEditarProduto = hasPerm("products:edit");
  // Ajuste de Recursos — Personalização de Cores, item 7 — só gate os
  // controles de COR/tema; nome fantasia/slogan continuam sempre editáveis.
  const canPersonalizeCores = hasFeature("personalizacao_cores");
  const [planInfoOpen, setPlanInfoOpen] = useState(false);
  // Reorganização do menu (delta 2026-09-16) + correção de regressão —
  // "Usuários e permissões", "Tipos de serviço", "Grupos" e "Produtos/
  // Sistemas" são acessados pelo menu lateral (Cadastros) via
  // /configuracoes?tab=X, e renderizam como páginas STANDALONE (sem
  // título "Configurações", sem o menu interno/settingsNav, sem o card
  // "Status da configuração") — nunca mais como subseção de Configurações.
  // `standaloneTab` é recalculado A CADA RENDER a partir de searchParams
  // (useSearchParams já reage a toda navegação, inclusive só de query) —
  // corrige o bug anterior, em que um useState com inicializador
  // preguiçoso só lia o ?tab= UMA vez: como a rota /configuracoes não
  // remonta o componente ao trocar só a query, clicar em itens diferentes
  // (ex.: de "usuarios" para "tipos") reaproveitava a mesma instância e o
  // valor antigo ficava preso, sempre mostrando o primeiro tab aberto.
  const STANDALONE_TAB_TITLES = { usuarios: "Usuários e permissões", tipos: "Tipos de serviço", grupos: "Grupos", produtos: "Produtos/Sistemas" };
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const standaloneTab = STANDALONE_TAB_TITLES[tabParam] ? tabParam : null;
  // activeTab governa só o menu INTERNO de Configurações (settingsNav,
  // abaixo) — Geral/Identidade visual/Bancos e pagamentos/E-mail de
  // cobrança/Fiscal-NFS-e/Integrações/Auditoria. Os 4 itens movidos nunca
  // fazem parte dele; settingsNav não tem mais entradas para eles (grupo
  // "Operação" removido) — "Configurações" sempre abre em "Geral",
  // nunca mais preso num ?tab= de visita anterior.
  const [activeTab, setActiveTab] = useState("empresa");
  // Correção de regressão pré-produção (delta 2026-09-16) — a instância do
  // componente NUNCA remonta ao alternar entre /configuracoes e
  // /configuracoes?tab=X (mesma rota, só a query muda), então uma seleção
  // feita no menu INTERNO (ex.: "Bancos e pagamentos") ficava presa em
  // `activeTab` mesmo depois de visitar um item standalone de Cadastros e
  // voltar para "Configurações" puro — violava a garantia documentada
  // acima ("sempre abre em Geral"). Corrigido: toda vez que a URL deixa de
  // ter um ?tab= de item standalone (ou seja, a tela principal de
  // Configurações volta a aparecer), `activeTab` é reiniciado para
  // "empresa". Nunca dispara enquanto o usuário só navega pelo menu
  // interno (standaloneTab permanece null o tempo todo nesse caso, então a
  // dependência abaixo não muda).
  useEffect(() => { if (!standaloneTab) setActiveTab("empresa"); }, [standaloneTab]);
  const [company, setCompany] = useState({ razao_social: "", nome_fantasia: "", cnpj: "", endereco: "", telefone: "", email: "", logo_url: "", require_approval: false });
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  // Fundação Multi-Provider de Pagamentos — contas de pagamento REAIS do
  // tenant (Mercado Pago e futuros provedores). O cadastro legado "Bancos"
  // (boleto simulado/mock) foi retirado da experiência do tenant — o
  // mecanismo mock continua no backend, usado só por testes/dev.
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [paccOpen, setPaccOpen] = useState(false);
  const [paccEditing, setPaccEditing] = useState(null);
  const [paccForm, setPaccForm] = useState({ display_name: "", provider: "mercado_pago", environment: "test", access_token: "", webhook_secret: "" });
  const [paccTesting, setPaccTesting] = useState(null);
  // PIX por chave (QR estático, sem Mercado Pago/API bancária) — modalidade
  // INDEPENDENTE do PIX integrado acima; nunca reaproveita paymentAccounts.
  const [pixKeyAccounts, setPixKeyAccounts] = useState([]);
  const [pkaOpen, setPkaOpen] = useState(false);
  const [pkaEditing, setPkaEditing] = useState(null);
  const [pkaForm, setPkaForm] = useState({ name: "", institution_name: "", holder_name: "", key_type: "cpf", pix_key: "", active: true });
  const [pkaShowKey, setPkaShowKey] = useState(false);
  const [pkaRevealOpen, setPkaRevealOpen] = useState(null); // { key_type, pix_key }
  const [pkaRevealing, setPkaRevealing] = useState(null);
  // Pacote Final de Ajustes, Parte G — E-mail de cobrança POR TENANT
  // (mesma UX já homologada no Admin Master, escopada à própria empresa —
  // smtp_password nunca vem do backend, só password_configured).
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailSettingsForm, setEmailSettingsForm] = useState({
    active: true, from_name: "", from_email: "", reply_to: "", smtp_host: "", smtp_port: "587",
    security: "ssl_tls", smtp_username: "", smtp_password: "",
  });
  const [emailSettingsEditing, setEmailSettingsEditing] = useState(false);
  const [testingEmailConnection, setTestingEmailConnection] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [audit, setAudit] = useState([]);
  const [uOpen, setUOpen] = useState(false);
  const [tOpen, setTOpen] = useState(false);
  const [pOpen, setPOpen] = useState(false);
  const [gOpen, setGOpen] = useState(false);
  const [uForm, setUForm] = useState({ name: "", email: "", password: "", role: "analista", active: true, group_ids: [] });
  const [tNome, setTNome] = useState("");
  const [pForm, setPForm] = useState({ nome: "", group_id: "", active: true });
  const [editingProduct, setEditingProduct] = useState(null);
  const [gForm, setGForm] = useState({ nome: "", active: true });
  const [editingGroup, setEditingGroup] = useState(null);
  const [productGroupFilter, setProductGroupFilter] = useState("");
  // Item 4 — filtro Ativos/Inativos/Todos, padrão Ativos, reutilizado nas
  // três listagens desta página que já têm um campo `active` (Usuários,
  // Produtos/Sistemas, Grupos). Tipos de serviço não tem conceito de
  // ativo/inativo hoje (só criar/excluir) — sem filtro aqui, por design.
  const [usersStatusFiltro, setUsersStatusFiltro] = useState(STATUS_FILTRO_DEFAULT);
  const [produtosStatusFiltro, setProdutosStatusFiltro] = useState(STATUS_FILTRO_DEFAULT);
  const [gruposStatusFiltro, setGruposStatusFiltro] = useState(STATUS_FILTRO_DEFAULT);
  const filteredUsers = useMemo(() => users.filter(u => statusFiltroPassa(usersStatusFiltro, u.active)), [users, usersStatusFiltro]);
  const filteredGroups = useMemo(() => groups.filter(g => statusFiltroPassa(gruposStatusFiltro, g.active !== false)), [groups, gruposStatusFiltro]);
  const filteredProducts = useMemo(() => products.filter(p => statusFiltroPassa(produtosStatusFiltro, p.active !== false)), [products, produtosStatusFiltro]);
  const [permUser, setPermUser] = useState(null);
  const [goalUser, setGoalUser] = useState(null);
  const [histUser, setHistUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [viewingAudit, setViewingAudit] = useState(null);
  const [auditFilters, setAuditFilters] = useState({ start: "", end: "", user: "", ops: [], entity: "", q: "" });
  // Personalização (Pacote 2, itens 1 e 2)
  const [branding, setBranding] = useState({ nome_fantasia: "", slogan: "", cor_principal: "", cor_destaque: "", cor_apoio: "", tema_fundo: "claro" });
  const [brandingSaving, setBrandingSaving] = useState(false);
  // Integração WhatsApp (Fase 1 — Fundação/Arquitetura). Nesta fase o status
  // é sempre "not_configured" (nenhuma conexão real com a Meta ainda existe)
  // — os demais estados (connecting/connected/error/disconnected) já têm
  // apresentação preparada aqui para quando a Fase 2 passar a produzi-los de
  // verdade, mas não são simulados.
  const [whatsappConfig, setWhatsappConfig] = useState({ status: "not_configured" });
  const [whatsappTestOpen, setWhatsappTestOpen] = useState(false);
  const [whatsappTestNumber, setWhatsappTestNumber] = useState("");
  const [whatsappTestSending, setWhatsappTestSending] = useState(false);

  const load = async () => {
    // Correções de homologação (P1) — nenhuma chamada protegida pode ficar
    // sem tratamento; um 403 (financeiro ou de permissão) aqui não pode
    // virar promise rejeitada sem tratamento.
    try {
    const [c, u, t, pr, gr, a, br, wa, pacc, pka, es] = await Promise.all([
      api.get("/config/company"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/config/service-types"),
      api.get("/config/products").catch(() => ({ data: [] })),
      api.get("/config/groups").catch(() => ({ data: [] })),
      // Antes, qualquer falha aqui (401/403/500/rede) era silenciosamente
      // convertida em lista vazia — a grade de Auditoria aparecia "carregada
      // normalmente" (colunas OK) mas sem nenhuma linha, indistinguível de
      // "o tenant realmente não tem histórico". Agora o erro real é
      // mostrado (mesmo padrão de formatApiError já usado no resto da
      // tela), então uma falha de verdade fica visível e diagnosticável em
      // vez de mascarada como "sem eventos".
      // Rota /audit-log (renomeada de /audit por bloqueio de ad-blocker) e
      // a lógica de isolamento por tenant/scope="platform" já foram
      // homologadas — este pacote só evolui a INTERFACE por cima disso,
      // sem tocar na rota nem no filtro do backend novamente.
      isAdmin ? api.get("/audit-log").catch(e => { toast.error(formatApiError(e)); return { data: [] }; }) : Promise.resolve({ data: [] }),
      api.get("/branding").catch(() => ({ data: null })),
      isAdmin && hasFeature("whatsapp_integration") ? api.get("/config/integrations/whatsapp").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      api.get("/config/payment-accounts").catch(() => ({ data: [] })),
      api.get("/config/pix-key-accounts").catch(() => ({ data: [] })),
      // Item 30 — só Admin do tenant (GET já é require_role("admin") no
      // backend; analista/supervisor não chega a pedir).
      isAdmin ? api.get("/config/email-settings").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]);
    setCompany({ ...company, ...c.data });
    setUsers(u.data); setTypes(t.data); setProducts(pr.data); setGroups(gr.data); setAudit(a.data);
    if (br.data) setBranding(br.data);
    if (wa.data) setWhatsappConfig(wa.data);
    setPaymentAccounts(pacc.data);
    setPixKeyAccounts(pka.data);
    if (es.data) {
      setEmailSettings(es.data);
      setEmailSettingsForm({
        active: es.data.active, from_name: es.data.from_name || "", from_email: es.data.from_email || "",
        reply_to: es.data.reply_to || "", smtp_host: es.data.smtp_host || "", smtp_port: String(es.data.smtp_port || 587),
        security: es.data.security || "ssl_tls", smtp_username: es.data.smtp_username || "", smtp_password: "",
      });
    }
    } catch { /* Correções de homologação (P1) — nunca deixar a promise sem tratamento */ }
    // eslint-disable-next-line
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const saveCompany = async () => {
    // Pacote consolidado pós-homologação, Prioridade 1 — esta rota só
    // altera configuração/identidade interna do tenant (logo,
    // require_approval) — nunca o cadastro oficial: razão social/nome
    // fantasia/CNPJ/telefone/endereço/e-mail, que são somente leitura
    // aqui e só mudam pelo Admin Master.
    try {
      await api.put("/config/company", { logo_url: company.logo_url || "", require_approval: !!company.require_approval });
      toast.success("Configurações salvas");
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const onLogoFile = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const file_data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post("/config/company/logo", { file_name: file.name, file_data });
      setCompany({ ...company, logo_url: data.logo_url });
      toast.success("Logo atualizada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLogoUploading(false); }
  };

  const createUser = async () => {
    const pwErr = passwordPolicyError(uForm.password);
    if (pwErr) { toast.error(pwErr); return; }
    try {
      const body = { ...uForm, group_ids: uForm.role === "supervisor" ? uForm.group_ids : [] };
      await api.post("/users", body);
      toast.success("Usuário criado"); setUOpen(false);
      setUForm({ name: "", email: "", password: "", role: "analista", active: true, group_ids: [] });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const createType = async () => {
    try { await api.post("/config/service-types", { nome: tNome, active: true }); setTOpen(false); setTNome(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delType = async (id) => { await api.delete(`/config/service-types/${id}`); load(); };

  const openNewProduct = () => { setEditingProduct(null); setPForm({ nome: "", group_id: "", active: true }); setPOpen(true); };
  const openEditProduct = (p) => { setEditingProduct(p); setPForm({ nome: p.nome, group_id: p.group_id || "", active: p.active !== false }); setPOpen(true); };
  const saveProduct = async () => {
    if (!pForm.nome.trim()) return toast.error("Informe o nome do produto/sistema");
    if (!editingProduct && !pForm.group_id) return toast.error("Selecione um Grupo para o novo Produto/Sistema");
    try {
      if (editingProduct) await api.put(`/config/products/${editingProduct.id}`, pForm);
      else await api.post("/config/products", pForm);
      toast.success("Produto/Sistema salvo");
      setPOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleProduct = async (p) => {
    try { await api.put(`/config/products/${p.id}`, { nome: p.nome, group_id: p.group_id || "", active: !p.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Grupos (Pacote 2, item 3) ----------
  const openNewGroup = () => { setEditingGroup(null); setGForm({ nome: "", active: true }); setGOpen(true); };
  const openEditGroup = (g) => { setEditingGroup(g); setGForm({ nome: g.nome, active: g.active !== false }); setGOpen(true); };
  const saveGroup = async () => {
    if (!gForm.nome.trim()) return toast.error("Informe o nome do grupo");
    try {
      if (editingGroup) await api.put(`/config/groups/${editingGroup.id}`, gForm);
      else await api.post("/config/groups", gForm);
      toast.success("Grupo salvo");
      setGOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleGroup = async (g) => {
    try { await api.put(`/config/groups/${g.id}`, { nome: g.nome, active: !g.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const groupName = (gid) => groups.find(g => g.id === gid)?.nome || "-";

  // Fundação Multi-Provider de Pagamentos — Contas de Pagamento.
  const openNewPaymentAccount = () => {
    setPaccEditing(null);
    setPaccForm({ display_name: "", provider: "mercado_pago", environment: "test", access_token: "", webhook_secret: "" });
    setPaccOpen(true);
  };
  const openEditPaymentAccount = (acc) => {
    setPaccEditing(acc);
    setPaccForm({ display_name: acc.display_name, provider: acc.provider, environment: acc.environment, access_token: "", webhook_secret: "" });
    setPaccOpen(true);
  };
  const savePaymentAccount = async () => {
    if (!paccForm.display_name.trim()) return toast.error("Informe um nome para identificar esta conta");
    const body = { display_name: paccForm.display_name, provider: paccForm.provider, environment: paccForm.environment };
    // access_token/webhook_secret só vão no corpo quando preenchidos —
    // deixar em branco ao editar NUNCA sobrescreve uma credencial já salva
    // (mesmo comportamento do backend/PlatformBankProviderIn).
    if (paccForm.access_token.trim()) body.access_token = paccForm.access_token.trim();
    if (paccForm.webhook_secret.trim()) body.webhook_secret = paccForm.webhook_secret.trim();
    try {
      if (paccEditing) await api.put(`/config/payment-accounts/${paccEditing.id}`, body);
      else await api.post("/config/payment-accounts", body);
      toast.success("Conta de pagamento salva");
      setPaccOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const testPaymentAccountConnection = async (acc) => {
    setPaccTesting(acc.id);
    try {
      const { data } = await api.post(`/config/payment-accounts/${acc.id}/test-connection`);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPaccTesting(null); }
  };
  const setDefaultPaymentAccount = async (acc) => {
    try { await api.post(`/config/payment-accounts/${acc.id}/set-default`); toast.success(`"${acc.display_name}" definida como padrão`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const unsetDefaultPaymentAccount = async (acc) => {
    try { await api.post(`/config/payment-accounts/${acc.id}/unset-default`); toast.success(`"${acc.display_name}" deixou de ser padrão`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePaymentAccountActive = async (acc) => {
    try {
      await api.post(`/config/payment-accounts/${acc.id}/${acc.active ? "deactivate" : "activate"}`);
      toast.success(acc.active ? "Conta desativada" : "Conta ativada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Pacote Final de Ajustes, Parte G — E-mail de cobrança do TENANT (mesma
  // UX/fluxo do card já homologado no Admin Master, escopado à empresa
  // logada — nunca um company_id escolhido no frontend).
  const loadEmailSettings = async () => {
    try {
      const { data } = await api.get("/config/email-settings");
      setEmailSettings(data);
      setEmailSettingsForm({
        active: data.active, from_name: data.from_name || "", from_email: data.from_email || "",
        reply_to: data.reply_to || "", smtp_host: data.smtp_host || "", smtp_port: String(data.smtp_port || 587),
        security: data.security || "ssl_tls", smtp_username: data.smtp_username || "", smtp_password: "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEmailSettings = async () => {
    try {
      const { data } = await api.put("/config/email-settings", {
        active: emailSettingsForm.active,
        from_name: emailSettingsForm.from_name, from_email: emailSettingsForm.from_email,
        reply_to: emailSettingsForm.reply_to || null,
        smtp_host: emailSettingsForm.smtp_host, smtp_port: Number(emailSettingsForm.smtp_port) || 587,
        security: emailSettingsForm.security, smtp_username: emailSettingsForm.smtp_username,
        // Nunca envia "" como se fosse senha nova — vazio preserva a senha já salva.
        smtp_password: emailSettingsForm.smtp_password ? emailSettingsForm.smtp_password : null,
      });
      setEmailSettings(data);
      setEmailSettingsForm(f => ({ ...f, smtp_password: "" }));
      setEmailSettingsEditing(false);
      toast.success("E-mail de cobrança salvo");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const testEmailConnection = async () => {
    setTestingEmailConnection(true);
    try {
      const { data } = await api.post("/config/email-settings/test-connection");
      if (data.ok) toast.success(data.message); else toast.error(data.message);
      loadEmailSettings();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTestingEmailConnection(false); }
  };
  const sendTestEmail = async () => {
    if (!testEmailTo.trim()) { toast.error("Informe o e-mail de destino do teste"); return; }
    setSendingTestEmail(true);
    try {
      const { data } = await api.post("/config/email-settings/send-test", { to: testEmailTo.trim() });
      if (data.ok) toast.success(data.message); else toast.error(data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingTestEmail(false); }
  };
  const PAYMENT_PROVIDER_LABELS = { mercado_pago: "Mercado Pago", asaas: "Asaas" };
  const PAYMENT_CONNECTION_STATUS_META = {
    connected: { label: "Conectado", badge: "bg-emerald-100 text-emerald-800" },
    failed: { label: "Falha na conexão", badge: "bg-red-100 text-red-800" },
    not_tested: { label: "Não testado", badge: "bg-slate-100 text-slate-700" },
  };
  // PIX por chave — QR estático gerado 100% localmente (sem Mercado Pago/
  // API bancária). O backend nunca devolve a chave real fora de /reveal —
  // editar exige buscá-la de novo (o PUT substitui o registro inteiro).
  const openNewPixKeyAccount = () => {
    setPkaEditing(null);
    setPkaForm({ name: "", institution_name: "", holder_name: "", key_type: "cpf", pix_key: "", active: true });
    setPkaShowKey(false);
    setPkaOpen(true);
  };
  const openEditPixKeyAccount = async (acc) => {
    setPkaEditing(acc);
    setPkaForm({ name: acc.name, institution_name: acc.institution_name || "", holder_name: acc.holder_name, key_type: acc.key_type, pix_key: "", active: acc.active !== false });
    setPkaShowKey(false);
    setPkaOpen(true);
    try {
      const { data } = await api.post(`/config/pix-key-accounts/${acc.id}/reveal`);
      setPkaForm(f => ({ ...f, pix_key: data.pix_key, key_type: data.key_type }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const savePixKeyAccount = async () => {
    if (!pkaForm.name.trim()) return toast.error("Informe um nome para identificar esta configuração");
    if (!pkaForm.holder_name.trim()) return toast.error("Informe o titular da conta");
    if (!pkaForm.pix_key.trim()) return toast.error("Informe a chave Pix");
    const body = {
      name: pkaForm.name, institution_name: pkaForm.institution_name.trim() || undefined,
      holder_name: pkaForm.holder_name, key_type: pkaForm.key_type,
      pix_key: pkaForm.pix_key, active: pkaForm.active,
    };
    try {
      if (pkaEditing) await api.put(`/config/pix-key-accounts/${pkaEditing.id}`, body);
      else await api.post("/config/pix-key-accounts", body);
      toast.success("Chave Pix salva");
      setPkaOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePixKeyAccountActive = async (acc) => {
    try {
      await api.post(`/config/pix-key-accounts/${acc.id}/${acc.active ? "deactivate" : "activate"}`);
      toast.success(acc.active ? "Chave desativada" : "Chave ativada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setDefaultPixKeyAccount = async (acc) => {
    try { const { data } = await api.post(`/config/pix-key-accounts/${acc.id}/set-default`); setPixKeyAccounts(data); toast.success(`"${acc.name}" definida como padrão`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const unsetDefaultPixKeyAccount = async (acc) => {
    try { const { data } = await api.post(`/config/pix-key-accounts/${acc.id}/unset-default`); setPixKeyAccounts(data); toast.success(`"${acc.name}" deixou de ser padrão`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const revealPixKey = async (acc) => {
    setPkaRevealing(acc.id);
    try { const { data } = await api.post(`/config/pix-key-accounts/${acc.id}/reveal`); setPkaRevealOpen(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setPkaRevealing(null); }
  };
  const PIX_KEY_TYPE_LABELS = { cpf: "CPF", cnpj: "CNPJ", email: "E-mail", telefone: "Telefone", aleatoria: "Chave aleatória" };

  const toggleUser = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); load(); };

  // ---------- Personalização (Pacote 2, itens 1 e 2) ----------
  const saveBranding = async () => {
    for (const [label, val] of [["Cor principal", branding.cor_principal], ["Cor de destaque", branding.cor_destaque], ["Cor de apoio", branding.cor_apoio]]) {
      if (val && !isValidHex(val)) return toast.error(`${label}: informe uma cor hexadecimal válida (ex.: #2563EB)`);
    }
    setBrandingSaving(true);
    try {
      const { data } = await api.put("/config/branding", branding);
      setBranding({ nome_fantasia: data.nome_fantasia, slogan: data.slogan, cor_principal: data.cor_principal, cor_destaque: data.cor_destaque, cor_apoio: data.cor_apoio, tema_fundo: data.tema_fundo });
      applyBrandingVars(data);
      toast.success("Personalização salva");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBrandingSaving(false); }
  };
  const resetBranding = async () => {
    setBrandingSaving(true);
    try {
      const { data } = await api.post("/config/branding/reset");
      setBranding(b => ({ ...b, cor_principal: data.cor_principal, cor_destaque: data.cor_destaque, cor_apoio: data.cor_apoio, tema_fundo: data.tema_fundo }));
      applyBrandingVars(data);
      toast.success("Personalização restaurada ao padrão");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBrandingSaving(false); }
  };

  // ---------- Integração WhatsApp (Fase 1 — Fundação) ----------
  // "Conectar WhatsApp" fica desabilitado nesta fase (a rota existe e já
  // está protegida corretamente, mas responde "configuração pendente" —
  // ver connect_whatsapp no backend); não há simulação de conexão aqui.
  const sendWhatsappTestMessage = async () => {
    if (!whatsappTestNumber.trim()) return toast.error("Informe um número de WhatsApp para o teste");
    setWhatsappTestSending(true);
    try {
      await api.post("/config/integrations/whatsapp/test-message", { to: whatsappTestNumber });
      toast.success("Mensagem de teste enviada");
      setWhatsappTestOpen(false); setWhatsappTestNumber("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWhatsappTestSending(false); load(); }
  };

  // ---------- Auditoria: filtros aplicados 100% no que já foi carregado ----------
  // (a rota /audit-log já limita a 500 eventos do próprio tenant, mais
  // recentes primeiro — filtrar em memória evita qualquer nova mudança na
  // rota/isolamento, que já foram homologados neste bloco anterior).
  const auditUsersOptions = useMemo(() => [...new Set(audit.map(a => a.user_email).filter(Boolean))].sort(), [audit]);
  const auditOpsOptions = useMemo(() => [...new Set(audit.map(a => a.op).filter(Boolean))].sort(), [audit]);
  const auditEntityOptions = useMemo(() => [...new Set(audit.map(a => a.entity).filter(Boolean))].sort(), [audit]);
  // Correção visual — item 2 do ajuste: eventos sem user_name (registros
  // antigos) tentam achar o nome ATUAL do usuário nos dados JÁ CARREGADOS
  // (`users`, mesma lista da aba "Usuários e permissões" — nenhuma
  // chamada nova) via user_id, antes de cair para o e-mail.
  const usersById = useMemo(() => Object.fromEntries((users || []).map(u => [u.id, u])), [users]);
  const AUDIT_ROLE_LABELS = { admin: "Administrador", supervisor: "Supervisor", analista: "Analista" };
  // Item 1 — a coluna deve mostrar só o nome LIMPO: alguns registros
  // (inclusive o admin inicial provisionado pelo backend, ver
  // ADMIN_EMAIL/bootstrap) têm o cargo dentro do próprio campo `name`
  // (ex.: "João Marcos (Administrador)") — nunca corrigido aqui no dado
  // em si (backend/cadastro do usuário continuam intocados), só
  // removido da EXIBIÇÃO da tabela; o valor completo permanece
  // disponível no tooltip.
  const stripRoleSuffix = (name) => (name || "").replace(/\s*\([^)]*\)\s*$/, "").trim();
  const auditUserDisplay = (a) => {
    const matched = a.user_id ? usersById[a.user_id] : null;
    const rawName = a.user_name || matched?.name || "";
    const cleanName = stripRoleSuffix(rawName);
    const email = a.user_email || a.email || matched?.email || "";
    const role = matched?.role ? AUDIT_ROLE_LABELS[matched.role] : null;
    const label = cleanName || email || "-";
    const title = [cleanName || null, email || null, role].filter(Boolean).join(" — ") || label;
    return { label, title };
  };
  const filteredAudit = useMemo(() => {
    const q = auditFilters.q.trim().toLowerCase();
    return audit.filter(a => {
      if (auditFilters.start && (a.at || "") < auditFilters.start) return false;
      if (auditFilters.end && (a.at || "") > `${auditFilters.end}T23:59:59`) return false;
      if (auditFilters.user && a.user_email !== auditFilters.user) return false;
      if (auditFilters.ops.length && !auditFilters.ops.includes(a.op)) return false;
      if (auditFilters.entity && a.entity !== auditFilters.entity) return false;
      if (q) {
        const hay = [a.op, opLabel(a.op), a.entity, entityLabel(a.entity), a.entity_id, a.user_email, a.user_name].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [audit, auditFilters]);
  const clearAuditFilters = () => setAuditFilters({ start: "", end: "", user: "", ops: [], entity: "", q: "" });
  const toggleOpFilter = (op) => setAuditFilters(f => ({
    ...f, ops: f.ops.includes(op) ? f.ops.filter(o => o !== op) : [...f.ops, op],
  }));

  // Menu vertical interno de Configurações (Delta visual) — agrupa as MESMAS
  // abas existentes; a visibilidade por papel/recurso é idêntica à antiga
  // barra horizontal (isAdmin / hasFeature). Nenhuma funcionalidade removida.
  const settingsNav = [
    {
      group: "Empresa",
      items: [
        { value: "empresa", label: "Geral", Icon: Building2, show: true },
        { value: "personalizacao", label: "Identidade visual", Icon: ImageIcon, show: true },
      ],
    },
    {
      group: "Financeiro",
      items: [
        { value: "bancos", label: "Bancos e pagamentos", Icon: Landmark, show: true },
        { value: "email-cobranca", label: "E-mail de cobrança", Icon: Mail, show: isAdmin },
        { value: "fiscal-nfse", label: "Fiscal / NFS-e", Icon: FileText, show: isAdmin && hasFeature("emissao_nfse") },
      ],
    },
    {
      group: "Integrações e controle",
      items: [
        { value: "integracoes", label: "Integrações", Icon: Link2, show: isAdmin && hasFeature("whatsapp_integration") },
        { value: "auditoria", label: "Auditoria", Icon: ScrollText, show: isAdmin },
      ],
    },
  ];

  // Painel "Status da configuração" — só apresentação, derivado de dados JÁ
  // carregados (nada de nova chamada/lógica de negócio).
  const statusItems = [
    { label: "Dados da empresa", done: !!company.cnpj },
    { label: "Identidade visual", done: !!company.logo_url },
    ...(isAdmin ? [{ label: "E-mail de cobrança", done: !!emailSettings }] : []),
    ...(isAdmin && hasFeature("whatsapp_integration") ? [{ label: "Integrações", done: whatsappConfig?.status === "connected" }] : []),
  ];
  const statusDone = statusItems.filter((i) => i.done).length;
  const statusPct = statusItems.length ? Math.round((statusDone / statusItems.length) * 100) : 0;

  // Conteúdo real de cada um dos 4 itens movidos (delta 2026-09-16) —
  // extraído para funções locais para que a MESMA lógica/JSX/handlers
  // sirvam a página standalone abaixo, sem duplicar nada; não existem
  // mais como TabsContent dentro do <Tabs> de Configurações (settingsNav
  // não referencia esses values, então nunca seriam alcançáveis por lá).
  const renderUsuariosContent = () => (
    <>
      <Card className="p-0 overflow-hidden">
        <div className="p-4 flex justify-between items-center flex-wrap gap-2">
          <div className="font-heading font-semibold">Usuários e Acessos</div>
          <div className="flex items-center gap-2">
            <StatusFiltro value={usersStatusFiltro} onChange={setUsersStatusFiltro} testId="usuarios-filtro-status" />
            {isAdmin && <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setUOpen(true)} data-testid="user-novo"><Plus size={16} /> Novo</Button>}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="py-3 px-4">Nome</th><th>E-mail</th><th>Perfil</th><th>Situação</th><th className="text-right pr-4">Ações</th></tr></thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="py-3 px-4 font-medium">{u.name}</td>
                <td>{u.email}</td>
                <td><Badge className="bg-slate-100 text-slate-800 capitalize">{u.role}</Badge></td>
                <td><Badge className={u.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{u.active ? "Ativo" : "Inativo"}</Badge></td>
                <td className="pr-4">
                  {/* Ajuste visual — coluna de ações em slots de largura fixa
                      (mesma ordem/handlers de sempre): cada ação ocupa sempre
                      a mesma posição/largura, mesmo quando ausente para uma
                      linha (perfil/permissão), evitando que os ícones
                      "pulem" de lugar entre as linhas. */}
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    <div className="w-8 flex justify-center shrink-0">
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditUser(u)} title="Editar usuário" data-testid={`edit-btn-${u.email}`}>
                          <Pencil size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="min-w-[92px] flex justify-end shrink-0">
                      {isAdmin && (u.role === "analista" || u.role === "supervisor") && (
                        <Button size="sm" variant="ghost" className="h-8 w-full justify-center gap-1" onClick={() => setGoalUser(u)} title="Meta mensal" data-testid={`goal-btn-${u.email}`}>
                          <Target size={14} /> {u.meta_mensal ? money(u.meta_mensal) : "Meta"}
                        </Button>
                      )}
                    </div>
                    <div className="w-8 flex justify-center shrink-0">
                      {(u.role === "analista" || u.role === "supervisor") && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setHistUser(u)} title="Histórico 6 meses" data-testid={`hist-btn-${u.email}`}>
                          <TrendingUp size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="min-w-[130px] flex justify-end shrink-0">
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-8 w-full justify-center gap-1" onClick={() => setPermUser(u)} title="Permissões" data-testid={`perm-btn-${u.email}`}>
                          <Shield size={14} /> <span className="truncate">Permissões{u.role === "supervisor" && u.group_ids?.length ? ` (${u.group_ids.length} grupo${u.group_ids.length > 1 ? "s" : ""})` : ""}</span>
                        </Button>
                      )}
                    </div>
                    <div className="w-[84px] flex justify-end shrink-0">
                      {isAdmin && u.email !== user.email && (
                        <Button size="sm" variant="ghost" className="h-8 w-full justify-center" onClick={() => toggleUser(u)}>{u.active ? "Bloquear" : "Ativar"}</Button>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Dialog open={uOpen} onOpenChange={setUOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo usuário</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input value={uForm.name} onChange={e => setUForm({ ...uForm, name: e.target.value })} data-testid="uform-name" /></div>
            <div><Label>E-mail</Label><Input type="email" value={uForm.email} onChange={e => setUForm({ ...uForm, email: e.target.value })} data-testid="uform-email" /></div>
            <div>
              <Label>Senha inicial</Label>
              <Input type="password" value={uForm.password} onChange={e => setUForm({ ...uForm, password: e.target.value })} data-testid="uform-pw" />
              <p className="text-xs text-slate-400 mt-1">{PASSWORD_HELP_TEXT}</p>
            </div>
            <div>
              <Label>Perfil</Label>
              <Select value={uForm.role} onValueChange={v => setUForm({ ...uForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="analista">Analista</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {uForm.role === "supervisor" && (
            <div className="mt-3">
              <Label>Grupos autorizados (opcional)</Label>
              <p className="text-xs text-slate-500 mb-1.5">Sem nenhum marcado, o supervisor não tem restrição de Grupo.</p>
              <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1">
                {groups.length === 0 && <div className="text-xs text-slate-400">Nenhum Grupo cadastrado ainda.</div>}
                {groups.map(g => (
                  <label key={g.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={uForm.group_ids.includes(g.id)}
                      onChange={() => setUForm(f => ({ ...f, group_ids: f.group_ids.includes(g.id) ? f.group_ids.filter(x => x !== g.id) : [...f.group_ids, g.id] }))}
                      data-testid={`uform-group-${g.id}`}
                    />
                    {g.nome}
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setUOpen(false)}>Cancelar</Button><Button onClick={createUser} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="uform-save">Criar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const renderTiposContent = () => (
    <>
      <Card className="p-0 overflow-hidden">
        <div className="p-4 flex justify-between items-center">
          <div className="font-heading font-semibold">Tipos de Serviço</div>
          {isAdmin && <Button onClick={() => setTOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus size={16} /> Novo</Button>}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="py-3 px-4">Nome</th><th className="text-right pr-4">Ações</th></tr></thead>
          <tbody>
            {types.map(t => (
              <tr key={t.id} className="border-t border-slate-100"><td className="py-3 px-4">{t.nome}</td>
                <td className="text-right pr-4">{isAdmin && <Button size="sm" variant="ghost" onClick={() => delType(t.id)}><Trash2 size={14} /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo tipo de serviço</DialogTitle></DialogHeader>
          <Label>Nome</Label><Input value={tNome} onChange={e => setTNome(e.target.value)} />
          <DialogFooter><Button variant="outline" onClick={() => setTOpen(false)}>Cancelar</Button><Button onClick={createType} className="bg-primary hover:bg-primary/90 text-primary-foreground">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const renderGruposContent = () => (
    <>
      <Card className="p-0 overflow-hidden">
        <div className="p-4 flex justify-between items-center">
          <div>
            <div className="font-heading font-semibold">Grupos</div>
            <p className="text-xs text-slate-500">Organizam Produtos/Sistemas e a autorização de Supervisores. Nome único dentro do seu tenant.</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusFiltro value={gruposStatusFiltro} onChange={setGruposStatusFiltro} testId="grupos-filtro-status" />
            {podeCriarGrupo && <Button onClick={openNewGroup} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="grupo-novo"><Plus size={16} /> Novo</Button>}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="py-3 px-4">Nome</th><th>Situação</th><th className="text-right pr-4">Ações</th></tr></thead>
          <tbody>
            {filteredGroups.map(g => (
              <tr key={g.id} className="border-t border-slate-100">
                <td className="py-3 px-4">{g.nome}</td>
                <td><Badge className={g.active !== false ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{g.active !== false ? "Ativo" : "Inativo"}</Badge></td>
                <td className="text-right pr-4 space-x-1">
                  {podeEditarGrupo && <Button size="sm" variant="ghost" onClick={() => openEditGroup(g)} title="Editar" data-testid={`grupo-editar-${g.id}`}><Pencil size={14} /></Button>}
                  {podeEditarGrupo && (
                    <Button size="sm" variant="ghost" onClick={() => toggleGroup(g)} title={g.active !== false ? "Inativar" : "Ativar"} data-testid={`grupo-toggle-${g.id}`}>
                      {g.active !== false ? <PowerOff size={14} /> : <Power size={14} />}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {filteredGroups.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-slate-400">{groups.length === 0 ? "Nenhum grupo cadastrado" : "Nenhum grupo para este filtro"}</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={gOpen} onOpenChange={setGOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingGroup ? "Editar grupo" : "Novo grupo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={gForm.nome} onChange={e => setGForm({ ...gForm, nome: e.target.value })} data-testid="grupo-form-nome" /></div>
            {editingGroup && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={gForm.active} onChange={e => setGForm({ ...gForm, active: e.target.checked })} />
                Ativo
              </label>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGOpen(false)}>Cancelar</Button><Button onClick={saveGroup} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="grupo-form-salvar">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const renderProdutosContent = () => (
    <>
      <Card className="p-0 overflow-hidden">
        <div className="p-4 flex justify-between items-center flex-wrap gap-2">
          <div>
            <div className="font-heading font-semibold">Produtos/Sistemas</div>
            <p className="text-xs text-slate-500">Catálogo usado no campo "Produto/Sistema" da Ficha de Serviço — só itens ativos aparecem na seleção.</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusFiltro value={produtosStatusFiltro} onChange={setProdutosStatusFiltro} testId="produtos-filtro-status" />
            <Select value={productGroupFilter || "todos"} onValueChange={v => setProductGroupFilter(v === "todos" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="produto-filtro-grupo"><SelectValue placeholder="Filtrar por Grupo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os Grupos</SelectItem>
                {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.nome}</SelectItem>)}
                <SelectItem value="__sem_grupo__">Sem grupo</SelectItem>
              </SelectContent>
            </Select>
            {podeCriarProduto && <Button onClick={openNewProduct} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="produto-novo"><Plus size={16} /> Novo</Button>}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="py-3 px-4">Nome</th><th>Grupo</th><th>Situação</th><th className="text-right pr-4">Ações</th></tr></thead>
          <tbody>
            {filteredProducts
              .filter(p => !productGroupFilter || (productGroupFilter === "__sem_grupo__" ? !p.group_id : p.group_id === productGroupFilter))
              .sort((a, b) => groupName(a.group_id).localeCompare(groupName(b.group_id)) || a.nome.localeCompare(b.nome))
              .map(p => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="py-3 px-4">{p.nome}</td>
                <td className="text-slate-600">{p.group_id ? groupName(p.group_id) : <span className="text-slate-400">Sem grupo</span>}</td>
                <td><Badge className={p.active !== false ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{p.active !== false ? "Ativo" : "Inativo"}</Badge></td>
                <td className="text-right pr-4 space-x-1">
                  {podeEditarProduto && <Button size="sm" variant="ghost" onClick={() => openEditProduct(p)} title="Editar" data-testid={`produto-editar-${p.id}`}><Pencil size={14} /></Button>}
                  {podeEditarProduto && (
                    <Button size="sm" variant="ghost" onClick={() => toggleProduct(p)} title={p.active !== false ? "Inativar" : "Ativar"} data-testid={`produto-toggle-${p.id}`}>
                      {p.active !== false ? <PowerOff size={14} /> : <Power size={14} />}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {filteredProducts.filter(p => !productGroupFilter || (productGroupFilter === "__sem_grupo__" ? !p.group_id : p.group_id === productGroupFilter)).length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-slate-400">{products.length === 0 ? "Nenhum produto/sistema cadastrado" : "Nenhum produto/sistema para este filtro"}</td></tr>
            )}
          </tbody>
        </table>
      </Card>
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingProduct ? "Editar produto/sistema" : "Novo produto/sistema"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={pForm.nome} onChange={e => setPForm({ ...pForm, nome: e.target.value })} data-testid="produto-form-nome" /></div>
            <div>
              <Label>Grupo{!editingProduct && " *"}</Label>
              <Select value={pForm.group_id || "none"} onValueChange={v => setPForm({ ...pForm, group_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="produto-form-grupo"><SelectValue placeholder="Selecione o Grupo" /></SelectTrigger>
                <SelectContent>
                  {!editingProduct ? null : <SelectItem value="none">Sem grupo</SelectItem>}
                  {groups.filter(g => g.active !== false || g.id === pForm.group_id).map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.nome}{g.active === false ? " (inativo)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {groups.filter(g => g.active !== false).length === 0 && (
                <p className="text-xs text-amber-700 mt-1">Cadastre um Grupo na aba Grupos antes de criar um novo Produto/Sistema.</p>
              )}
            </div>
            {editingProduct && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={pForm.active} onChange={e => setPForm({ ...pForm, active: e.target.checked })} />
                Ativo
              </label>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPOpen(false)}>Cancelar</Button><Button onClick={saveProduct} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="produto-form-salvar">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // Correção de regressão (delta 2026-09-16) — quando chegou aqui por um
  // dos 4 itens de Cadastros, a página é SÓ o título + o conteúdo real
  // daquele cadastro: nunca o título "Configurações", nunca o menu
  // interno (settingsNav), nunca o card "Status da configuração" — eles
  // deixaram de parecer subseção de Configurações, como pedido. Mesmos
  // componentes/handlers/estado de sempre (renderXContent acima), só
  // apresentados fora do "casco" de Configurações.
  if (standaloneTab) {
    const renderers = { usuarios: renderUsuariosContent, tipos: renderTiposContent, grupos: renderGruposContent, produtos: renderProdutosContent };
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-bold">{STANDALONE_TAB_TITLES[standaloneTab]}</h1>
        </div>
        {renderers[standaloneTab]()}
        {/* Hotfix (delta 2026-09-19) — REGRESSÃO real confirmada no código
            (não só hipótese): estes 4 dialogs (Editar usuário/Permissões/
            Meta/Histórico) só existiam no `return` PRINCIPAL do componente,
            bem abaixo (fora deste `if`, que retorna antes de chegar lá).
            Os botões dentro de `renderUsuariosContent()` (Editar/
            Permissões/Meta/Histórico) sempre chamaram exatamente os mesmos
            `setEditUser`/`setPermUser`/`setGoalUser`/`setHistUser` de
            sempre — o estado mudava normalmente, só não existia NENHUM
            componente de Dialog montado na árvore para reagir a ele
            quando a página é a standalone "Usuários e permissões": o
            clique no ícone de editar "não fazia nada" porque o Dialog que
            deveria abrir nunca chegava a ser renderizado neste branch.
            Tipos/Grupos/Produtos NÃO tinham esse problema — o dialog de
            criar/editar de cada um já é local à própria função
            renderXContent (gOpen/pOpen/tOpen, acima), sempre presente
            aqui. Renderizados aqui incondicionalmente (e não só quando
            standaloneTab === "usuarios"): o `open` de cada um já é
            `!!estado` — nas páginas de Tipos/Grupos/Produtos esse estado
            nunca é setado (nenhum botão de lá os aciona), então ficam
            sempre fechados; simples e sem duplicar a condição de qual
            standalone está ativo. */}
        <PermissionsDialog user={permUser} groups={groups} open={!!permUser} onOpenChange={(v) => !v && setPermUser(null)} onSaved={load} />
        <GoalDialog user={goalUser} open={!!goalUser} onOpenChange={(v) => !v && setGoalUser(null)} onSaved={load} />
        <AnalystHistoryDialog analystId={histUser?.id} open={!!histUser} onOpenChange={(v) => !v && setHistUser(null)} />
        <EditUserDialog user={editUser} open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)} onSaved={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Configurações</h1>
          <p className="text-slate-500 text-sm">Empresa, usuários, tipos de serviço, produtos, bancos e auditoria</p>
        </div>
        {/* Planos/Recursos (item 8) — indicador discreto do plano contratado
            pela PRÓPRIA empresa (sempre a do usuário logado; nunca outra).
            Clicar abre os recursos disponíveis; a empresa nunca pode alterar
            o próprio plano por aqui — isso é exclusivo do Admin Master. */}
        {plan?.plan && (
          <button type="button" onClick={() => setPlanInfoOpen(true)} data-testid="plan-indicator"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-sm hover:bg-slate-50 shrink-0">
            <Sparkles size={14} className="text-primary" />
            <span className="text-slate-500">Plano atual —</span>
            <span className="font-semibold text-slate-900">{plan.plan.nome}</span>
          </button>
        )}
      </div>

      <Dialog open={planInfoOpen} onOpenChange={setPlanInfoOpen}>
        <DialogContent data-testid="plan-info-dialog">
          <DialogHeader><DialogTitle>Plano contratado</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-slate-500">Plano atual</div>
              <div className="font-heading text-lg font-semibold">{plan?.plan?.nome || "-"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-2">Recursos disponíveis</div>
              <ul className="space-y-1.5">
                {(plan?.resources || []).map(r => (
                  <li key={r.codigo} className="flex items-center gap-2 text-sm">
                    {r.available ? <Check size={15} className="text-emerald-600 shrink-0" /> : <X size={15} className="text-slate-300 shrink-0" />}
                    <span className={r.available ? "text-slate-900" : "text-slate-400 line-through"}>{r.nome}</span>
                    {r.available && r.origin === "override_enabled" && (
                      <span className="text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Adicional contratado</span>
                    )}
                    {!r.available && r.origin === "override_disabled" && (
                      <span className="text-xs text-slate-400">Recurso não disponível para esta empresa</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col lg:flex-row gap-5 items-start">
        {/* Menu vertical interno — substitui o excesso de abas horizontais */}
        <Card className="w-full lg:w-[250px] lg:shrink-0 p-2 lg:sticky lg:top-4" data-testid="settings-nav">
          <nav className="space-y-4">
            {settingsNav.map((grp) => {
              const items = grp.items.filter((i) => i.show);
              if (!items.length) return null;
              return (
                <div key={grp.group}>
                  <div className="px-2.5 pb-1 pt-1 text-[11px] font-semibold tracking-wider text-slate-400 uppercase">{grp.group}</div>
                  <div className="space-y-0.5">
                    {items.map(({ value, label, Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setActiveTab(value)}
                        data-testid={`nav-${value}`}
                        className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-left transition ${activeTab === value ? "bg-primary/10 text-primary font-medium" : "text-slate-600 hover:bg-slate-50"}`}
                      >
                        <Icon size={16} className="shrink-0" />
                        <span className="truncate">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
        </Card>

        {/* Coluna central de conteúdo */}
        <div className="flex-1 min-w-0 w-full space-y-4">

        <TabsContent value="empresa" className="mt-4">
          <Card className="p-5">
            {/* Pacote consolidado pós-homologação, Prioridade 1 — cadastro
                OFICIAL do tenant (fonte de Admin Master → Empresas/Gestão
                Comercial/Faturamento SaaS/boleto/PIX). Somente leitura
                aqui: editar exige o Admin Master (/admin/companies), nunca
                mais esta tela — alterar o "nome interno" NÃO pode mais
                mudar o que a plataforma vê como nome oficial. */}
            <div className="mb-4 p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-500">
              Dados cadastrais oficiais (razão social, nome fantasia, CNPJ, telefone, endereço, e-mail) são
              controlados pela LOOTUS e não podem ser alterados por aqui. Para corrigir algum desses dados,
              contate o suporte/Admin Master. O nome exibido internamente no menu lateral pode ser
              personalizado na aba <b>Personalização</b>.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Razão Social</Label><Input value={company.razao_social} disabled className="bg-slate-50 text-slate-500" data-testid="cfg-razao-social-readonly" /></div>
              <div><Label>Nome Fantasia (oficial)</Label><Input value={company.nome_fantasia} disabled className="bg-slate-50 text-slate-500" data-testid="cfg-nome-fantasia-readonly" /></div>
              <div><Label>CNPJ</Label><Input value={company.cnpj} disabled className="bg-slate-50 text-slate-500" data-testid="cfg-cnpj-readonly" /></div>
              <div><Label>Telefone</Label><Input value={company.telefone} disabled className="bg-slate-50 text-slate-500" data-testid="cfg-telefone-readonly" /></div>
              <div className="md:col-span-2"><Label>Endereço</Label><Input value={company.endereco} disabled className="bg-slate-50 text-slate-500" data-testid="cfg-endereco-readonly" /></div>
              <div><Label>E-mail</Label><Input value={company.email} disabled className="bg-slate-50 text-slate-500" data-testid="cfg-email-readonly" /></div>
              <div className="md:col-span-2">
                <Label>Logo</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                  <div>
                    <Input value={company.logo_url?.startsWith("data:") ? "" : company.logo_url} placeholder={company.logo_url?.startsWith("data:") ? "Logo enviada por arquivo" : "URL da logo"} onChange={e => setCompany({ ...company, logo_url: e.target.value })} />
                    <p className="text-xs text-slate-500 mt-1">Ou informe a URL de uma imagem já publicada.</p>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm border border-dashed border-slate-300 rounded-md px-3 py-2 cursor-pointer hover:bg-slate-50">
                      <Upload size={16} />
                      {logoUploading ? "Enviando..." : "Selecionar arquivo do computador"}
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="logo-file-input"
                        onChange={e => onLogoFile(e.target.files?.[0])} disabled={logoUploading} />
                    </label>
                    <p className="text-xs text-slate-500 mt-1">
                      Formatos aceitos: PNG, JPEG ou WEBP. Tamanho máximo: {LOGO_MAX_MB}MB.
                      Recomendado: imagem quadrada ou proporção 1:1 a 2:1, fundo transparente, mínimo 200×200px.
                    </p>
                  </div>
                </div>
                {company.logo_url && (
                  <div className="mt-2 p-2 border border-slate-200 rounded-md inline-block bg-slate-50">
                    <img src={company.logo_url} alt="Logo da empresa" className="h-12 object-contain" />
                  </div>
                )}
              </div>
              <div className="md:col-span-2 mt-2 p-3 rounded-md bg-slate-50 border border-slate-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={!!company.require_approval} onChange={e => setCompany({ ...company, require_approval: e.target.checked })} disabled={!isAdmin} data-testid="cfg-require-approval" className="mt-1" />
                  <div>
                    <div className="font-medium text-slate-900">Exigir aprovação do supervisor</div>
                    <div className="text-xs text-slate-500">Quando ativado, fichas encerradas por analistas com supervisor atribuído ficam com status <b>Aguardando aprovação</b> até o supervisor aprovar. Só então são disponibilizadas para faturamento e o e-mail de assinatura é enviado ao cliente.</div>
                  </div>
                </label>
              </div>
            </div>
            {isAdmin && <div className="mt-4"><Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={saveCompany} data-testid="save-company">Salvar</Button></div>}
          </Card>
        </TabsContent>

        <TabsContent value="personalizacao" className="mt-4">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-1"><Palette size={18} className="text-primary" /><div className="font-heading font-semibold">Identidade visual do seu ambiente</div></div>
            <p className="text-xs text-slate-500 mb-4">
              Personalize como o LOOTUS aparece para a sua equipe: nome exibido, logo e cores. A logo é definida na aba <b>Empresa</b> — aqui você ajusta o nome exibido e as cores. Aplicado só ao seu tenant; nunca afeta outras empresas.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label>Nome Fantasia exibido</Label>
                  <Input value={branding.nome_fantasia} onChange={e => setBranding({ ...branding, nome_fantasia: e.target.value })} placeholder={company.razao_social || "Nome exibido no menu lateral"} data-testid="branding-nome-fantasia" />
                  <p className="text-xs text-slate-500 mt-1">Se vazio, usa a Razão Social cadastrada em Empresa.</p>
                </div>
                <div>
                  <Label>Slogan / Subtítulo (opcional)</Label>
                  <Input value={branding.slogan} onChange={e => setBranding({ ...branding, slogan: e.target.value })} placeholder="Ex.: Tecnologia que impulsiona resultados" maxLength={80} data-testid="branding-slogan" />
                  <p className="text-xs text-slate-500 mt-1">Aparece como segunda linha abaixo do nome, no menu lateral. Se vazio, nada é exibido nessa linha.</p>
                </div>

                {/* Ajuste de Recursos — Personalização de Cores, itens 6/7:
                    nome fantasia/slogan (acima) são identidade básica —
                    sempre editáveis, em qualquer plano. Cores/tema abaixo só
                    ficam editáveis com o recurso "personalizacao_cores"
                    contratado; sem ele, mostramos os valores atuais
                    desabilitados (nada é apagado) + uma explicação, em vez
                    de simplesmente sumir com a seção. */}
                {!canPersonalizeCores && (
                  <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500" data-testid="branding-cores-bloqueado">
                    Personalização de cores não está disponível no plano contratado da sua empresa. Fale com o administrador da plataforma para contratar este recurso.
                  </div>
                )}

                {[
                  { key: "cor_principal", label: "Cor principal", hint: "Menu, item ativo e botões principais." },
                  { key: "cor_destaque", label: "Cor de destaque", hint: "Links e elementos de destaque." },
                  { key: "cor_apoio", label: "Cor de apoio", hint: "Elementos visuais secundários." },
                ].map(({ key, label, hint }) => (
                  <div key={key}>
                    <Label>{label}</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={isValidHex(branding[key]) ? branding[key] : "#2563EB"}
                        onChange={e => setBranding({ ...branding, [key]: e.target.value })}
                        disabled={!canPersonalizeCores}
                        className="w-10 h-9 rounded border border-slate-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid={`branding-${key}-picker`}
                      />
                      <Input
                        value={branding[key]}
                        onChange={e => setBranding({ ...branding, [key]: e.target.value })}
                        placeholder="#2563EB (padrão LOOTUS)"
                        maxLength={7}
                        disabled={!canPersonalizeCores}
                        data-testid={`branding-${key}-hex`}
                        className={branding[key] && !isValidHex(branding[key]) ? "border-red-400" : ""}
                      />
                    </div>
                    {branding[key] && !isValidHex(branding[key]) && <p className="text-xs text-red-600 mt-1">Informe um código hexadecimal válido, ex.: #2563EB</p>}
                    <p className="text-xs text-slate-500 mt-1">{hint}</p>
                  </div>
                ))}

                <div>
                  <Label>Fundo / tema base</Label>
                  <Select value={branding.tema_fundo} onValueChange={v => setBranding({ ...branding, tema_fundo: v })} disabled={!canPersonalizeCores}>
                    <SelectTrigger data-testid="branding-tema"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claro">Claro (padrão)</SelectItem>
                      <SelectItem value="neutro">Neutro</SelectItem>
                      <SelectItem value="suave">Suave (leve tom da cor principal)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {isAdmin && (
                  <div className="flex gap-2 pt-2">
                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={saveBranding} disabled={brandingSaving} data-testid="branding-save">Salvar personalização</Button>
                    {canPersonalizeCores && (
                      <Button variant="outline" onClick={resetBranding} disabled={brandingSaving} data-testid="branding-reset"><RotateCcw size={14} /> Restaurar padrão</Button>
                    )}
                  </div>
                )}
              </div>

              {/* Pré-visualização em tempo real — usa o estado do formulário
                  (ainda não salvo), nunca as variáveis CSS globais, para não
                  aplicar uma cor inválida/incompleta no resto da tela
                  enquanto o admin está digitando. */}
              <div>
                <Label>Pré-visualização</Label>
                <PersonalizacaoPreview branding={branding} nomeFallback={company.razao_social} logoUrl={company.logo_url} />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="bancos" className="mt-4 space-y-6">
          <Card className="p-0 overflow-hidden">
            <div className="p-4 flex justify-between items-center">
              <div>
                <div className="font-heading font-semibold">Contas para Cobrança</div>
                <p className="text-sm text-slate-500 mt-0.5">Utilize suas próprias credenciais para emitir cobranças diretamente na sua conta.</p>
              </div>
              {isAdmin && <Button onClick={openNewPaymentAccount} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="nova-conta-pagamento"><Plus size={16} /> Nova conta</Button>}
            </div>
            <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 gap-3">
              {paymentAccounts.length === 0 && (
                <div className="col-span-full py-6 text-center text-slate-400 text-sm">Nenhuma conta de pagamento configurada ainda.</div>
              )}
              {paymentAccounts.map(acc => (
                <div key={acc.id} className="border border-slate-200 rounded-lg p-4" data-testid={`conta-pagamento-${acc.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{acc.display_name}</div>
                      <div className="text-xs text-slate-500">{PAYMENT_PROVIDER_LABELS[acc.provider] || acc.provider}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={acc.environment === "test" ? "bg-amber-100 text-amber-800" : "bg-slate-800 text-white"}>
                        {acc.environment === "test" ? "Ambiente de teste" : "Produção"}
                      </Badge>
                      {acc.is_default && <Badge className="bg-indigo-100 text-indigo-800">Conta padrão</Badge>}
                      {!acc.active && <Badge className="bg-slate-200 text-slate-600">Desativada</Badge>}
                    </div>
                  </div>
                  <div className="mt-2 text-sm flex items-center gap-2">
                    <span className="text-slate-500">Status:</span>
                    <Badge className={(PAYMENT_CONNECTION_STATUS_META[acc.connection_status] || PAYMENT_CONNECTION_STATUS_META.not_tested).badge}>
                      {(PAYMENT_CONNECTION_STATUS_META[acc.connection_status] || PAYMENT_CONNECTION_STATUS_META.not_tested).label}
                    </Badge>
                    {!acc.credential_configured && <span className="text-xs text-amber-700">(sem credencial configurada)</span>}
                  </div>
                  {/* Segurança Pré-Produção, Pacote 2 (P1-3/item 12) — webhook de
                      pagamento passou a exigir autenticação obrigatória; sem
                      segredo configurado, eventos automáticos simplesmente não
                      são processados (geração de boleto/PIX continua normal,
                      é uma capacidade separada). */}
                  <div className="mt-1.5 text-sm flex items-center gap-2">
                    <span className="text-slate-500">Webhook de pagamento:</span>
                    <Badge className={acc.webhook_secret_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                      {acc.webhook_secret_configured ? "Configurado" : "Não configurado"}
                    </Badge>
                  </div>
                  {!acc.webhook_secret_configured && (
                    <p className="mt-1 text-xs text-amber-700">
                      Eventos automáticos de pagamento não serão processados até que a autenticação do webhook seja configurada.
                    </p>
                  )}
                  {isAdmin && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditPaymentAccount(acc)} data-testid={`configurar-conta-${acc.id}`}>Configurar</Button>
                      <Button size="sm" variant="outline" onClick={() => testPaymentAccountConnection(acc)} disabled={paccTesting === acc.id || !acc.credential_configured} data-testid={`testar-conexao-${acc.id}`}>
                        {paccTesting === acc.id ? "Testando..." : "Testar conexão"}
                      </Button>
                      {acc.active && !acc.is_default && (
                        <Button size="sm" variant="outline" onClick={() => setDefaultPaymentAccount(acc)} data-testid={`definir-padrao-${acc.id}`}>Definir como padrão</Button>
                      )}
                      {acc.is_default && (
                        <Button size="sm" variant="outline" onClick={() => unsetDefaultPaymentAccount(acc)}>Remover como padrão</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => togglePaymentAccountActive(acc)} data-testid={`toggle-ativo-conta-${acc.id}`}>
                        {acc.active ? "Desativar" : "Ativar"}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Dialog open={paccOpen} onOpenChange={setPaccOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
                  <span>{paccEditing ? "Editar conta de pagamento" : "Nova conta de pagamento"}</span>
                  {paccForm.provider === "asaas" && (
                    <a href={ASAAS_GUIDE_URL} target="_blank" rel="noopener noreferrer"
                       className="text-xs font-normal text-primary hover:underline inline-flex items-center gap-1"
                       data-testid="asaas-guia-configuracao-link">
                      <ExternalLink size={12} /> Como configurar o Asaas?
                    </a>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>Nome</Label><Input value={paccForm.display_name} onChange={e => setPaccForm({ ...paccForm, display_name: e.target.value })} placeholder={paccForm.provider === "asaas" ? "Ex.: Asaas — Principal" : "Ex.: Mercado Pago — Principal"} /></div>
                <div>
                  <Label>Provedor</Label>
                  <Select value={paccForm.provider} onValueChange={v => setPaccForm({ ...paccForm, provider: v })} disabled={!!paccEditing}>
                    <SelectTrigger data-testid="conta-pagamento-form-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mercado_pago">Mercado Pago</SelectItem>
                      <SelectItem value="asaas">Asaas</SelectItem>
                    </SelectContent>
                  </Select>
                  {paccEditing && <p className="text-xs text-slate-500 mt-1">O provedor não pode ser alterado depois de criada a conta.</p>}
                </div>
                <div>
                  <Label>Ambiente</Label>
                  <Select value={paccForm.environment} onValueChange={v => setPaccForm({ ...paccForm, environment: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="test">{paccForm.provider === "asaas" ? "Sandbox" : "Teste"}</SelectItem>
                      <SelectItem value="production">Produção</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chave de API</Label>
                  <Input type="password" value={paccForm.access_token} onChange={e => setPaccForm({ ...paccForm, access_token: e.target.value })}
                    placeholder={paccEditing?.credential_configured ? "•••••••••••••••••••• (deixe em branco para manter)" : `Cole aqui a chave de API do ${PAYMENT_PROVIDER_LABELS[paccForm.provider]}`} />
                  {paccEditing?.credential_configured && <p className="text-xs text-slate-500 mt-1">Uma credencial já está configurada. Só é possível trocá-la — nunca visualizá-la de volta.</p>}
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Label>Segredo do Webhook</Label>
                    {paccForm.provider === "asaas" && (
                      <a href={ASAAS_GUIDE_URL} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                         data-testid="asaas-guia-webhook-link">
                        <ExternalLink size={12} /> Como configurar o webhook?
                      </a>
                    )}
                  </div>
                  {/* Item 6 — URL oficial do Webhook, só exibida quando a
                      arquitetura já consegue determiná-la com segurança
                      (backend calcula a partir de FRONTEND_URL/
                      public_app_url — a MESMA fonte única de todo link
                      público do sistema — nunca um domínio inventado
                      aqui). Só existe depois que a conta já foi criada
                      (precisa do id) e só para Asaas; se o backend não
                      puder determinar a URL neste ambiente, webhook_url
                      vem null e este bloco simplesmente não aparece. */}
                  {paccForm.provider === "asaas" && paccEditing?.webhook_url && (
                    <div className="mb-2">
                      <div className="flex gap-2">
                        <Input readOnly value={paccEditing.webhook_url} className="font-mono text-xs bg-slate-50" data-testid="asaas-webhook-url" />
                        <Button
                          type="button" variant="outline" size="sm" className="shrink-0"
                          onClick={() => { navigator.clipboard.writeText(paccEditing.webhook_url); toast.success("URL do Webhook copiada"); }}
                          data-testid="asaas-webhook-url-copiar"
                        >
                          <Copy size={14} /> Copiar URL
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Cole esta URL no campo "URL" do Webhook, na área de Integrações do Asaas.</p>
                    </div>
                  )}
                  <Input type="password" value={paccForm.webhook_secret} onChange={e => setPaccForm({ ...paccForm, webhook_secret: e.target.value })}
                    placeholder={paccEditing?.webhook_secret_configured ? "•••••••••••••••••••• (deixe em branco para manter)" : "Cole aqui o segredo/token de Webhook"} />
                  <p className="text-xs text-slate-500 mt-1">
                    {paccForm.provider === "asaas"
                      ? "Token enviado pelo Asaas no header \"asaas-access-token\" de cada Webhook."
                      : "Assinatura de segurança do Webhook do Mercado Pago."}
                    {" "}Eventos automáticos de pagamento (baixa/confirmação) só são processados com este segredo configurado corretamente — sem ele, o webhook é rejeitado. Não afeta a geração de boleto/PIX.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPaccOpen(false)}>Cancelar</Button>
                <Button onClick={savePaymentAccount} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="salvar-conta-pagamento">Salvar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* PIX por chave — modalidade INDEPENDENTE do PIX integrado acima
              (hoje via Mercado Pago, mas a arquitetura é multiprovedor):
              QR estático gerado 100% pela LOOTUS, sem integração bancária
              nem credenciais de banco. Nunca aparece misturado à lista de
              Contas de Pagamento — painel próprio, cor própria (violeta),
              para nunca ser confundido com o PIX integrado. */}
          <Card className="p-0 overflow-hidden border-violet-200" data-testid="pix-por-chave-card">
            <div className="p-4 flex justify-between items-start gap-3 border-b border-violet-100 bg-violet-50/60">
              <div>
                <div className="font-heading font-semibold flex items-center gap-2"><QrCode size={16} className="text-violet-600" /> PIX por chave</div>
                <p className="text-sm text-slate-600 mt-0.5 max-w-2xl">
                  Não exige integração bancária. A confirmação do pagamento é manual ou por conciliação.
                  No PIX integrado, o status pode ser atualizado automaticamente pelo provedor configurado.
                </p>
              </div>
              {isAdmin && <Button onClick={openNewPixKeyAccount} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0" data-testid="nova-chave-pix"><Plus size={16} /> Nova chave</Button>}
            </div>
            <div className="p-4 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {pixKeyAccounts.length === 0 && (
                <div className="col-span-full py-6 text-center text-slate-400 text-sm">Nenhuma chave Pix configurada ainda.</div>
              )}
              {pixKeyAccounts.map(acc => (
                <div key={acc.id} className="border border-slate-200 rounded-lg p-4" data-testid={`chave-pix-${acc.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{acc.name}</div>
                      <div className="text-xs text-slate-500">{acc.institution_name ? `${acc.institution_name} — ` : ""}{acc.holder_name}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {acc.is_default && <Badge className="bg-indigo-100 text-indigo-800">Padrão</Badge>}
                      {!acc.active && <Badge className="bg-slate-200 text-slate-600">Desativada</Badge>}
                    </div>
                  </div>
                  <div className="mt-2 text-sm space-y-0.5">
                    <div><span className="text-slate-500">{PIX_KEY_TYPE_LABELS[acc.key_type] || acc.key_type}:</span> <span className="font-mono">{acc.pix_key_masked}</span></div>
                  </div>
                  {isAdmin && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditPixKeyAccount(acc)} data-testid={`editar-chave-pix-${acc.id}`}><Pencil size={14} /> Editar</Button>
                      <Button size="sm" variant="outline" onClick={() => revealPixKey(acc)} disabled={pkaRevealing === acc.id} data-testid={`ver-chave-pix-${acc.id}`}>
                        <Eye size={14} /> {pkaRevealing === acc.id ? "Buscando..." : "Ver chave"}
                      </Button>
                      {acc.active && !acc.is_default && (
                        <Button size="sm" variant="outline" onClick={() => setDefaultPixKeyAccount(acc)} data-testid={`definir-padrao-chave-${acc.id}`}>Definir como padrão</Button>
                      )}
                      {acc.is_default && (
                        <Button size="sm" variant="outline" onClick={() => unsetDefaultPixKeyAccount(acc)}>Remover como padrão</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => togglePixKeyAccountActive(acc)} data-testid={`toggle-ativo-chave-${acc.id}`}>
                        {acc.active ? "Desativar" : "Ativar"}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Dialog open={pkaOpen} onOpenChange={setPkaOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{pkaEditing ? "Editar chave Pix" : "Nova chave Pix"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nome da configuração</Label><Input value={pkaForm.name} onChange={e => setPkaForm({ ...pkaForm, name: e.target.value })} placeholder="Ex.: Chave principal" data-testid="chave-pix-form-nome" /></div>
                <div><Label>Banco/instituição (opcional — apenas informativo)</Label><Input value={pkaForm.institution_name} onChange={e => setPkaForm({ ...pkaForm, institution_name: e.target.value })} placeholder="Ex.: Banco XP" /></div>
                <div><Label>Titular da conta</Label><Input value={pkaForm.holder_name} onChange={e => setPkaForm({ ...pkaForm, holder_name: e.target.value })} data-testid="chave-pix-form-titular" /></div>
                <div>
                  <Label>Tipo de chave</Label>
                  <Select value={pkaForm.key_type} onValueChange={v => setPkaForm({ ...pkaForm, key_type: v })}>
                    <SelectTrigger data-testid="chave-pix-form-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnpj">CNPJ</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="telefone">Telefone</SelectItem>
                      <SelectItem value="aleatoria">Chave aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chave Pix</Label>
                  <div className="flex gap-2">
                    <Input type={pkaShowKey ? "text" : "password"} value={pkaForm.pix_key} onChange={e => setPkaForm({ ...pkaForm, pix_key: e.target.value })} placeholder="Cole aqui a chave Pix" data-testid="chave-pix-form-chave" />
                    <Button type="button" variant="outline" size="icon" onClick={() => setPkaShowKey(v => !v)} title={pkaShowKey ? "Ocultar" : "Mostrar"}>
                      {pkaShowKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </Button>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={pkaForm.active} onChange={e => setPkaForm({ ...pkaForm, active: e.target.checked })} /> Ativo
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPkaOpen(false)}>Cancelar</Button>
                <Button onClick={savePixKeyAccount} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="salvar-chave-pix">Salvar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={!!pkaRevealOpen} onOpenChange={v => !v && setPkaRevealOpen(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Chave Pix</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <div className="text-xs text-slate-500">{PIX_KEY_TYPE_LABELS[pkaRevealOpen?.key_type] || pkaRevealOpen?.key_type}</div>
                <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded-md p-3 break-all" data-testid="chave-pix-revelada">{pkaRevealOpen?.pix_key}</div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setPkaRevealOpen(null)}>Fechar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Pacote Final de Ajustes, Parte G/R — E-mail de cobrança da
            PRÓPRIA empresa (usado ao enviar boleto/PIX/faturamento aos
            PRÓPRIOS clientes do tenant — nunca a mensalidade SaaS, que
            continua exclusivamente com o e-mail da Codexpy). */}
        {isAdmin && (
          <TabsContent value="email-cobranca" className="mt-4">
            <Card className="p-5 max-w-lg space-y-3" data-testid="config-email-cobranca">
              <div className="flex items-center justify-between">
                <div className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Mail size={16} /> E-mail de cobrança</div>
                {emailSettings?.password_configured ? (
                  <Badge className="bg-emerald-100 text-emerald-800">Configurado</Badge>
                ) : (
                  <Badge className="bg-slate-200 text-slate-700">Não configurado</Badge>
                )}
              </div>
              <p className="text-sm text-slate-500">
                Identidade e conexão SMTP usadas para enviar cobranças (boleto, PIX, faturamento) aos clientes desta empresa,
                em Faturamento &amp; Cobrança. Não afeta a mensalidade LOOTUS, que continua chegando pelo e-mail da Codexpy.
              </p>
              {!emailSettings?.password_configured && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Configure um e-mail de cobrança para permitir que o LOOTUS envie faturas, boletos, PIX e cobranças aos
                  clientes desta empresa. Sem essa configuração, o envio por e-mail fica indisponível — a Codexpy nunca
                  envia em nome da sua empresa.
                </p>
              )}

              {!emailSettingsEditing && (
                <div className="space-y-2">
                  <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
                    <div><span className="text-slate-500">Remetente:</span>{" "}
                      <span className="font-medium text-slate-900">
                        {emailSettings?.from_name || "-"} {emailSettings?.from_email ? `<${emailSettings.from_email}>` : ""}
                      </span>
                    </div>
                    <div><span className="text-slate-500">Reply-To:</span> <span className="font-medium text-slate-900">{emailSettings?.reply_to || "-"}</span></div>
                    <div><span className="text-slate-500">SMTP:</span> <span className="font-medium text-slate-900">{emailSettings?.smtp_host ? `${emailSettings.smtp_host}:${emailSettings.smtp_port}` : "-"}</span></div>
                    <div><span className="text-slate-500">Segurança:</span> <span className="font-medium text-slate-900">{SMTP_SECURITY_LABEL[emailSettings?.security] || "-"}</span></div>
                    <div><span className="text-slate-500">Usuário SMTP:</span> <span className="font-medium text-slate-900">{emailSettings?.smtp_username || "-"}</span></div>
                    <div><span className="text-slate-500">Credencial:</span> <span className="font-medium text-slate-900">{emailSettings?.password_configured ? "Configurada" : "Não configurada"}</span></div>
                    {emailSettings?.last_test_status && (
                      <div className="flex items-center gap-1.5 pt-1">
                        {emailSettings.last_test_status === "success" ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-red-600" />}
                        <span className="text-xs text-slate-500">
                          Último teste ({emailSettings.last_test_status === "success" ? "sucesso" : "falha"}
                          {emailSettings.last_test_at ? `, ${new Date(emailSettings.last_test_at).toLocaleString("pt-BR")}` : ""}): {emailSettings.last_test_detail}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEmailSettingsEditing(true)} data-testid="config-email-editar">
                      <Pencil size={14} /> {emailSettings?.password_configured ? "Configurar" : "Configurar e-mail de cobrança"}
                    </Button>
                    {emailSettings?.password_configured && (
                      <Button size="sm" variant="outline" disabled={testingEmailConnection} onClick={testEmailConnection} data-testid="config-email-testar-conexao">
                        <Wifi size={14} /> {testingEmailConnection ? "Testando..." : "Testar conexão"}
                      </Button>
                    )}
                  </div>
                  {emailSettings?.password_configured && (
                    <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-100">
                      <div className="flex-1 min-w-[200px]">
                        <Label>Enviar e-mail de teste para</Label>
                        <Input placeholder="seu-email@exemplo.com" value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)} data-testid="config-email-teste-destino" />
                      </div>
                      <Button size="sm" variant="outline" disabled={sendingTestEmail} onClick={sendTestEmail} data-testid="config-email-enviar-teste">
                        <Mail size={14} /> {sendingTestEmail ? "Enviando..." : "Enviar e-mail de teste"}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {emailSettingsEditing && (
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Identidade</div>
                  <div>
                    <Label>Nome do remetente</Label>
                    <Input value={emailSettingsForm.from_name} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, from_name: e.target.value })} placeholder="Financeiro Dabar" data-testid="config-email-from-name" />
                  </div>
                  <div>
                    <Label>E-mail do remetente</Label>
                    <Input value={emailSettingsForm.from_email} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, from_email: e.target.value })} placeholder="financeiro@suaempresa.com.br" data-testid="config-email-from-email" />
                  </div>
                  <div>
                    <Label>Reply-To</Label>
                    <Input value={emailSettingsForm.reply_to} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, reply_to: e.target.value })} placeholder="financeiro@suaempresa.com.br" data-testid="config-email-reply-to" />
                  </div>

                  <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold pt-2">Conexão SMTP</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2 sm:col-span-1">
                      <Label>SMTP Host</Label>
                      <Input value={emailSettingsForm.smtp_host} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, smtp_host: e.target.value })} placeholder="smtp.seuprovedor.com.br" data-testid="config-email-smtp-host" />
                    </div>
                    <div>
                      <Label>SMTP Port</Label>
                      <Input type="number" value={emailSettingsForm.smtp_port} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, smtp_port: e.target.value })} placeholder="465" data-testid="config-email-smtp-port" />
                    </div>
                  </div>
                  <div>
                    <Label>Segurança</Label>
                    <Select value={emailSettingsForm.security} onValueChange={v => setEmailSettingsForm({ ...emailSettingsForm, security: v })}>
                      <SelectTrigger data-testid="config-email-security"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ssl_tls">SSL/TLS</SelectItem>
                        <SelectItem value="starttls">STARTTLS</SelectItem>
                        <SelectItem value="none">Sem TLS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    {/* Item 16 — identidade ≠ autenticação: o usuário SMTP
                        pode ser uma mailbox diferente do remetente
                        visível, quando o From é um alias. */}
                    <Label>Usuário SMTP <span className="font-normal text-slate-400">(mailbox real autorizada — pode ser diferente do e-mail do remetente, se este for um alias)</span></Label>
                    <Input value={emailSettingsForm.smtp_username} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, smtp_username: e.target.value })} placeholder="administrativo@suaempresa.com.br" data-testid="config-email-smtp-username" />
                  </div>
                  <div>
                    <Label>Senha SMTP</Label>
                    <Input type="password" value={emailSettingsForm.smtp_password} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, smtp_password: e.target.value })}
                      placeholder={emailSettings?.password_configured ? "Senha já configurada. Preencha somente para substituir." : "Digite a senha SMTP"} data-testid="config-email-smtp-password" />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={emailSettingsForm.active} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, active: e.target.checked })} data-testid="config-email-ativo" />
                    Ativo
                  </label>
                  <div className="flex gap-2 pt-2">
                    <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveEmailSettings} data-testid="config-email-salvar">Salvar</Button>
                    <Button variant="outline" onClick={() => { setEmailSettingsEditing(false); loadEmailSettings(); }} data-testid="config-email-cancelar">Cancelar</Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>
        )}

        {/* NFS-e Nacional, Fase 1 — componente dedicado (fluxo próprio de
            certificado A1 + configuração fiscal), mesmo padrão de extrair
            um bloco grande/autocontido em vez de inflar ainda mais este
            arquivo (já muito extenso). */}
        {isAdmin && hasFeature("emissao_nfse") && (
          <TabsContent value="fiscal-nfse" className="mt-4">
            <FiscalNfseConfig />
          </TabsContent>
        )}

        {isAdmin && hasFeature("whatsapp_integration") && (
          <TabsContent value="integracoes" className="mt-4">
            <Card className="p-5" data-testid="integracao-whatsapp">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                <div className="font-heading font-semibold flex items-center gap-2">
                  <MessageCircle size={18} className="text-emerald-600" /> WhatsApp
                </div>
                <Badge className={WHATSAPP_STATUS_META[whatsappConfig.status]?.badge || WHATSAPP_STATUS_META.not_configured.badge} data-testid="whatsapp-status-badge">
                  Status: {WHATSAPP_STATUS_META[whatsappConfig.status]?.label || "Não configurado"}
                </Badge>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Conecte o WhatsApp da sua empresa para enviar links e notificações aos clientes.
              </p>

              {whatsappConfig.status === "connected" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 text-sm">
                  <div><div className="text-xs text-slate-500">Número conectado</div><div className="font-medium">{whatsappConfig.connected_number || "-"}</div></div>
                  <div><div className="text-xs text-slate-500">Última validação</div><div className="font-medium">{whatsappConfig.last_validated_at ? new Date(whatsappConfig.last_validated_at).toLocaleString("pt-BR") : "-"}</div></div>
                </div>
              )}
              {whatsappConfig.status === "error" && whatsappConfig.last_error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  Último erro: {whatsappConfig.last_error}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline" disabled title="Disponível em uma próxima etapa"
                  data-testid="whatsapp-connect"
                >
                  <Link2 size={16} /> Conectar WhatsApp
                </Button>
                <Button
                  variant="outline" disabled={whatsappConfig.status !== "connected"}
                  title={whatsappConfig.status !== "connected" ? "Disponível após conectar a integração" : "Desconectar"}
                  data-testid="whatsapp-disconnect"
                >
                  Desconectar
                </Button>
                <Button
                  variant="outline" disabled={whatsappConfig.status !== "connected"}
                  title={whatsappConfig.status !== "connected" ? "Disponível após conectar a integração" : "Enviar mensagem de teste"}
                  onClick={() => setWhatsappTestOpen(true)}
                  data-testid="whatsapp-test-message"
                >
                  Enviar mensagem de teste
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-4">
                A conexão oficial com a WhatsApp Business Platform (Meta) será disponibilizada em uma próxima etapa — nesta fase a integração está preparada (configuração, permissões, histórico e auditoria), mas ainda não conectada.
              </p>
            </Card>

            <Dialog open={whatsappTestOpen} onOpenChange={v => { if (!v) { setWhatsappTestOpen(false); setWhatsappTestNumber(""); } }}>
              <DialogContent>
                <DialogHeader><DialogTitle>Enviar mensagem de teste</DialogTitle></DialogHeader>
                <div><Label>Número de WhatsApp</Label><Input value={whatsappTestNumber} onChange={e => setWhatsappTestNumber(e.target.value)} placeholder="(83) 99999-9999" data-testid="whatsapp-test-number-input" /></div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setWhatsappTestOpen(false); setWhatsappTestNumber(""); }}>Cancelar</Button>
                  <Button onClick={sendWhatsappTestMessage} disabled={whatsappTestSending} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="confirm-whatsapp-test-message">
                    {whatsappTestSending ? "Enviando..." : "Enviar"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="auditoria" className="mt-4">
            <Card className="p-0 overflow-hidden">
              <div className="p-4 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-heading font-semibold">Auditoria</div>
                  <p className="text-xs text-slate-500">{filteredAudit.length} de {audit.length} evento(s) — últimas 500 operações do seu tenant</p>
                </div>
              </div>

              {/* Filtros */}
              <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2 items-end border-b border-slate-100">
                <div><Label className="text-xs">Data inicial</Label><Input type="date" value={auditFilters.start} onChange={e => setAuditFilters({ ...auditFilters, start: e.target.value })} data-testid="audit-filtro-inicio" /></div>
                <div><Label className="text-xs">Data final</Label><Input type="date" value={auditFilters.end} onChange={e => setAuditFilters({ ...auditFilters, end: e.target.value })} data-testid="audit-filtro-fim" /></div>
                <div>
                  <Label className="text-xs">Usuário</Label>
                  <Select value={auditFilters.user || "todos"} onValueChange={v => setAuditFilters({ ...auditFilters, user: v === "todos" ? "" : v })}>
                    <SelectTrigger data-testid="audit-filtro-usuario"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {auditUsersOptions.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Recurso/Entidade</Label>
                  <Select value={auditFilters.entity || "todos"} onValueChange={v => setAuditFilters({ ...auditFilters, entity: v === "todos" ? "" : v })}>
                    <SelectTrigger data-testid="audit-filtro-entidade"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {auditEntityOptions.map(e => <SelectItem key={e} value={e}>{entityLabel(e)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tipo de operação</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full justify-start font-normal" data-testid="audit-filtro-operacao">
                        <Filter size={14} className="mr-1" /> {auditFilters.ops.length ? `${auditFilters.ops.length} selecionada(s)` : "Todas"}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-h-72 overflow-y-auto">
                      <DropdownMenuLabel>Tipos de operação</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {auditOpsOptions.map(op => (
                        <DropdownMenuCheckboxItem key={op} checked={auditFilters.ops.includes(op)} onCheckedChange={() => toggleOpFilter(op)}>
                          {opLabel(op)}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div>
                  <Label className="text-xs">Pesquisar</Label>
                  <div className="flex gap-1">
                    <Input placeholder="Texto livre..." value={auditFilters.q} onChange={e => setAuditFilters({ ...auditFilters, q: e.target.value })} data-testid="audit-filtro-texto" />
                    <Button variant="outline" size="icon" onClick={clearAuditFilters} title="Limpar filtros" data-testid="audit-filtro-limpar"><X size={14} /></Button>
                  </div>
                </div>
              </div>

              {/* Legenda de cores */}
              <div className="px-4 py-2 flex flex-wrap gap-3 border-b border-slate-100 bg-slate-50/50">
                {Object.entries(AUDIT_CATEGORY_META).filter(([k]) => k !== "outro").map(([k, meta]) => (
                  <div key={k} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span className={`w-2 h-2 rounded-full ${meta.dot}`} /> {meta.label}
                  </div>
                ))}
              </div>

              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                {/* Correção visual — larguras fixas via <colgroup> (mais
                    confiável que classe de largura no <th> em
                    table-layout:fixed, nunca depende do conteúdo pra
                    decidir a largura da coluna): só "Descrição" fica sem
                    <col> definido, absorvendo o espaço restante. */}
                <table className="w-full text-sm table-fixed">
                  <colgroup>
                    {/* Delta — Data/Hora e Usuário ainda ficavam apertados;
                        larguras aumentadas (item 3) para caber "pt-BR"
                        completo (dd/mm/aaaa hh:mm:ss) e nomes comuns sem
                        cortar. */}
                    <col style={{ width: "165px" }} />
                    <col style={{ width: "190px" }} />
                    <col style={{ width: "220px" }} />
                    <col style={{ width: "110px" }} />
                    <col />
                    <col style={{ width: "60px" }} />
                  </colgroup>
                  <thead className="bg-slate-50 text-left text-slate-600 sticky top-0">
                    <tr>
                      <th className="py-2 px-3">Data/Hora</th>
                      <th className="px-2">Usuário</th>
                      <th className="px-2">Operação</th>
                      <th className="px-2">Recurso</th>
                      <th className="px-2">Descrição</th>
                      <th className="text-right pr-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAudit.map(a => {
                      const cat = AUDIT_CATEGORY_META[opCategory(a.op)];
                      // Item 1/2 — nome LIMPO (nunca cargo/e-mail junto);
                      // fallback: nome atual do usuário nos dados já
                      // carregados, depois e-mail truncado. Tooltip mantém
                      // nome + e-mail + perfil completos.
                      const { label: usuarioLabel, title: usuarioTitle } = auditUserDisplay(a);
                      return (
                        <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 whitespace-nowrap">{fmtDateTime(a.at)}</td>
                          <td className="px-2 py-2 truncate" title={usuarioTitle}>{usuarioLabel}</td>
                          {/* Correção visual — item 2: badge de Operação NUNCA
                              é cortado/sobreposto — a coluna tem largura fixa
                              (190px) que cobre a maioria dos rótulos numa
                              linha só; rótulos raros mais longos quebram em
                              2-3 linhas (align-top) em vez de vazar pra cima
                              da coluna seguinte. Nunca "truncate" aqui — um
                              badge cortado no meio fica ilegível/quebrado. */}
                          <td className="px-2 py-2 align-top"><Badge className={`${cat.badge} whitespace-normal text-left`}>{opLabel(a.op)}</Badge></td>
                          <td className="px-2 py-2 truncate" title={entityLabel(a.entity)}>{entityLabel(a.entity)}</td>
                          <td className="px-2 py-2 text-slate-500 truncate" title={eventDescription(a)}>{eventDescription(a)}</td>
                          <td className="text-right pr-3">
                            <Button size="sm" variant="ghost" onClick={() => setViewingAudit(a)} title="Visualizar" data-testid={`audit-ver-${a.id}`}><Eye size={14} /></Button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredAudit.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Nenhum evento encontrado com os filtros atuais</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        )}
        </div>

        {/* Painel lateral direito — Status da configuração (apresentação).
            Item 3 do ajuste de Auditoria: a tabela de auditoria precisa de
            mais largura útil (colunas cortando/coluna Usuário larga demais)
            — o painel lateral só ocupa espaço à toa nessa aba (não mostra
            nada específico de auditoria), então some enquanto ela está
            ativa, devolvendo a largura inteira à área central. Nenhuma
            outra aba muda. */}
        {activeTab !== "auditoria" && (
        <aside className="w-full lg:w-[300px] lg:shrink-0 lg:sticky lg:top-4" data-testid="settings-status-panel">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Settings size={16} className="text-primary" />
              <div className="font-heading font-semibold text-slate-900 text-sm">Status da configuração</div>
            </div>
            <div className="flex items-start gap-4">
              <div className="relative w-16 h-16 shrink-0">
                <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                  <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e2e8f0" strokeWidth="3.2" />
                  <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#10b981" strokeWidth="3.2" strokeLinecap="round" strokeDasharray={`${statusPct}, 100`} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-700">{statusPct}%</div>
              </div>
              <div className="min-w-0">
                <div className="font-heading font-semibold text-slate-900 text-sm">{statusPct >= 100 ? "Tudo pronto!" : "Quase lá!"}</div>
                <p className="text-xs text-slate-500 leading-snug mt-0.5">
                  Sua empresa está bem configurada. Complete os itens abaixo para aproveitar todo o potencial do LOOTUS.
                </p>
              </div>
            </div>
            <ul className="mt-5 space-y-2.5">
              {statusItems.map((i) => (
                <li key={i.label} className="flex items-center gap-2.5 text-sm">
                  {i.done
                    ? <CheckCircle2 size={17} className="text-emerald-600 shrink-0" />
                    : <span className="w-[15px] h-[15px] rounded-full border-2 border-slate-200 shrink-0" />}
                  <span className={i.done ? "text-slate-700" : "text-slate-400"}>{i.label}</span>
                </li>
              ))}
            </ul>
          </Card>
        </aside>
        )}
      </Tabs>

      {/* Auditoria: visualização somente-consulta de um evento */}
      <Dialog open={!!viewingAudit} onOpenChange={v => !v && setViewingAudit(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Detalhes do evento</DialogTitle></DialogHeader>
          {viewingAudit && (
            <div className="space-y-3 text-sm">
              {/* Pacote 2, item 10 — limpeza visual do modal: sem código
                  técnico da operação no cabeçalho, sem código de entidade
                  entre parênteses, sem UUID/identificador interno. O valor
                  técnico continua disponível nos dados armazenados (para
                  diagnóstico), só não polui mais a interface do admin. */}
              <Badge className={`${AUDIT_CATEGORY_META[opCategory(viewingAudit.op)].badge} text-sm px-2.5 py-1`}>{opLabel(viewingAudit.op)}</Badge>
              <div className="text-slate-600">{eventDescription(viewingAudit)}</div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-slate-500">Recurso</div><div className="font-medium">{entityLabel(viewingAudit.entity)}</div></div>
                <div><div className="text-xs text-slate-500">Data e hora</div><div className="font-medium">{fmtDateTime(viewingAudit.at)}</div></div>
                <div className="col-span-2"><div className="text-xs text-slate-500">Usuário</div><div className="font-medium">{viewingAudit.user_name || "-"}</div><div className="text-slate-500">{viewingAudit.user_email || viewingAudit.email || "-"}</div></div>
              </div>

              {(viewingAudit.before || viewingAudit.after) ? (
                (() => {
                  const rows = diffFields(viewingAudit.before, viewingAudit.after);
                  if (!rows.length) {
                    return <div className="pt-3 border-t border-slate-100 text-slate-500 text-xs">Sem alterações de campos registradas neste evento.</div>;
                  }
                  return (
                    <div className="pt-3 border-t border-slate-100">
                      <div className="text-xs text-slate-500 mb-2">Antes → Depois</div>
                      <div className="space-y-2">
                        {rows.map(r => (
                          <div key={r.key} className="grid grid-cols-3 gap-2 text-xs bg-slate-50 rounded-md p-2">
                            <div className="font-medium text-slate-700">{fieldLabel(r.key)}</div>
                            <div className="text-red-700 line-through decoration-red-300">{fmtVal(r.before, r.key)}</div>
                            <div className="text-emerald-700">{fmtVal(r.after, r.key)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="pt-3 border-t border-slate-100 text-slate-500 text-xs">Este evento não possui detalhes de antes/depois.</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PermissionsDialog user={permUser} groups={groups} open={!!permUser} onOpenChange={(v) => !v && setPermUser(null)} onSaved={load} />
      <GoalDialog user={goalUser} open={!!goalUser} onOpenChange={(v) => !v && setGoalUser(null)} onSaved={load} />
      <AnalystHistoryDialog analystId={histUser?.id} open={!!histUser} onOpenChange={(v) => !v && setHistUser(null)} />
      <EditUserDialog user={editUser} open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)} onSaved={load} />
    </div>
  );
}
